// Fyncall Widget SDK - Enhanced Version with All Features
// Modular imports
import {
  // Utils - helpers
  escapeHtml as _escapeHtml,
  adjustColor as _adjustColor,
  isLightColor,
  generateUUID,
  debounce,
  throttle,
  deepMerge,
  // Utils - color helpers
  getColorCode as _getColorCode,
  // Utils - formatting
  formatTime as _formatTime,
  formatDate as _formatDate,
  formatPrice as _formatPrice,
  formatStatus as _formatStatus,
  formatRelativeTime,
  formatFileSize,
  // Utils - storage
  STORAGE_KEYS,
  SESSION_TTL,
  getOrCreateVisitorId,
  saveSession as saveSessionToStorage,
  getSavedSession,
  clearSavedSession,
  cacheMessages,
  getCachedMessages,
  appendMessageToCache,
  clearMessageCache,
  isSessionExpired,
  updateSessionActivity,
  updateLastSync,
  saveProactiveState as saveProactiveToStorage,
  loadProactiveState as loadProactiveFromStorage,
  getSavedTheme,
  saveTheme,
  getSoundEnabled,
  saveSoundEnabled,
  saveVisitorInfo as saveVisitorInfoToStorage,
  getVisitorInfo as getVisitorInfoFromStorage,
  trackVisit,
  getVisitCount,
  // Utils - message cache (IndexedDB)
  initMessageDB,
  getMessagesFromCache as getMessagesFromIndexedDB,
  saveMessagesToCache as saveMessagesToIndexedDB,
  appendMessageToIndexedDB,
  clearSessionMessages,
  clearOldMessages,
  // Core - config
  isNgrokUrl,
  initConfig,
  setTenantConfig,
  // Core - auth
  initAuth as _initAuth,
  isAuthenticated as _isAuthenticated,
  getToken as _getToken,
  getAuthHeaders as _getAuthHeaders,
  // Core - state
  setSession as setModuleSession,
  setVisitorToken as setModuleVisitorToken,
  setWidgetOpen as setModuleWidgetOpen,
  setSDKMode as setModuleSDKMode,
  // Core - API
  apiCreateSession,
  getSessionMessages,
  apiValidateSession,
  apiSendMessage,
  submitPrechatForm as _submitPrechatForm,
  submitCsatSurvey as _submitCsatSurvey,
  sendReaction as _sendReaction,
  uploadFile as _uploadFile,
  // Core - Session
  clearSession as _clearSession,
  checkExistingSession as _checkExistingSession,
  // Features - WebSocket
  websocketManager,
  connectWebSocket as _connectWebSocket,
  disconnectWebSocket as _disconnectWebSocket,
  onWebSocketMessage,
  onWebSocketStatus,
  markWebSocketSessionInvalid,
  resetWebSocketSession,
  // Features - Transcript
  collectMessagesForTranscript as _collectMessagesForTranscript,
  downloadTranscriptText as _downloadTranscriptText,
  downloadTranscriptPDF as _downloadTranscriptPDF,
  // Features - Messaging
  playNotificationSound as _playNotificationSound,
  resumeAudioContext as _resumeAudioContext,
  showDesktopNotification as _showDesktopNotification,
  // Integrations - Shopify
  isShopifyStore,
  detectShopifyContext as _detectShopifyContext,
  getShopifyCart,
  shopifyAddToCart as _shopifyAddToCart,
  shopifyUpdateCartItem as _shopifyUpdateCartItem,
  shopifyRemoveFromCart as _shopifyRemoveFromCart,
  clearShopifyCart as _clearShopifyCart,
  handleShopifyCartAction as _handleShopifyCartAction,
  getShopifyCheckoutUrl,
  goToCheckout as _goToCheckout,
  getShopifyContextForSession,
  buildCartContext,
  // Enhanced visitor detection
  detectShopifyCustomerFull as _detectShopifyCustomerFull,
  getOrDetectShopifyContext as _getOrDetectShopifyContext,
  autoIdentifyCustomer as _autoIdentifyCustomer,
  extractCustomFields as _extractCustomFields,
  getShopifyVisitorInfo as _getShopifyVisitorInfo,
  getShopifyContextForSessionFull as _getShopifyContextForSessionFull,
  // UI - Components
  LAUNCHER_ICONS,
  // UI - Components - Rich Messages
  createProductCarousel as _createProductCarousel,
  createProductCard as _createProductCard,
  createOrderStatusCard as _createOrderStatusCard,
  createOrderDetailCard as _createOrderDetailCard,
  createQuickActionCards as _createQuickActionCards,
  createCollectionBrowser as _createCollectionBrowser,
  createTicketStatusCard as _createTicketStatusCard,
  createComparisonTable as _createComparisonTable,
  getCategoryIcon as _getCategoryIcon,
  createInfoCard as _createInfoCard,
  createFeatureList as _createFeatureList,
  createCategoryBrowser as _createCategoryBrowser,
  // UI - Components - Cart Panel
  createCartPanelCard as _createCartPanelCard,
  updateCartPanelTotals as _updateCartPanelTotals,
  updateCartItemTotal as _updateCartItemTotal,
  showEmptyCartState as _showEmptyCartState,
  // UI - Forms
  shouldShowPrechatForm as _shouldShowPrechatForm,
  renderDynamicPrechatFields as _renderDynamicPrechatFields,
  showPrechatFormUI,
  hidePrechatFormUI,
  validatePrechatForm,
  createCsatState,
  configureCsatType,
  showCsatSurveyUI,
  handleStarRating as _handleStarRating,
  handleThumbsRating as _handleThumbsRating,
  handleNpsRating as _handleNpsRating,
  showCsatThankYou as _showCsatThankYou,
  closeCsatSurvey as _closeCsatSurvey,
  submitCsatToBackend,
  // UI - Styles
  generateAllStyles,
  generateWidgetCSS,
  // UI - Templates
  generateWidgetHTML,
  queryWidgetElements as _queryWidgetElements,
  // UI - Components - Home Screen
  getDefaultFaqs as _getDefaultFaqs,
  renderHomeFaqs as _renderHomeFaqs,
  initializeHomeFaqs,
  // Features - Notifications
  initDesktopNotifications,
  setWidgetFocused,
  // Features - Theme
  initTheme as _initTheme,
  setTheme as _setTheme,
  getTheme as _getTheme,
  toggleTheme as _toggleTheme,
  // UI - Components - Toast/Status
  showToast as _showToast,
  showStatus as _showStatus,
  // Features - Variant Modal
  openVariantModalState,
  closeVariantModalState,
  groupVariantOptions as _groupVariantOptions,
  findVariantByOption as _findVariantByOption,
  isOptionSelected as _isOptionSelected,
  selectVariantOption as _selectVariantOption,
  updateQuantity as _updateQuantity,
  getStockStatus,
  generateVariantModalHTML,
  handleVariantNotifyMe,
  requestSizeGuide,
  getAddToCartDetails,
  addToCart as _addToCart,
  // Features - Cart Operations
  calculateCartSummary as _calculateCartSummary,
  calculateFreeShippingProgress as _calculateFreeShippingProgress,
  calculateDeliveryEstimate as _calculateDeliveryEstimate,
  calculatePromoDiscount as _calculatePromoDiscount,
  validatePromoCode as _validatePromoCode,
  applyPromoCodeToShopify,
  generateAppliedPromoHTML,
  // UI - Event Bindings
  bindAllEvents as _bindAllEvents,
  // UI - Components - Product Detail
  createProductDetailCard as _createProductDetailCard,
  updateDetailQty as _updateDetailQty,
  addDetailToCart as _addDetailToCart,
  // UI - Components - Cart Rendering (Side Cart Panel)
  createSideCartItem as _createSideCartItem,
  createEmptySideCartHTML,
  createPromoFormHTML,
  createCheckoutButton as _createCheckoutButton,
  renderSideCartItems as _renderSideCartItems,
  updateSideCartDisplay as _updateSideCartDisplay,
  // Features - Proactive Engagement (enhanced)
  setupProactiveEngagement as _setupProactiveEngagement,
  loadProactiveSessionState as _loadProactiveSessionState,
  saveProactiveSessionState as _saveProactiveSessionState,
  getLocalProactiveState,
  matchesUrlPattern as _matchesUrlPattern,
  isWithinBusinessHours as _isWithinBusinessHours,
  isReturningVisitor as _isReturningVisitor,
  evaluateAndShowTrigger as _evaluateAndShowTrigger,
  // Constants
  FREE_SHIPPING_THRESHOLD,
  DELIVERY_MIN_BUSINESS_DAYS,
  DELIVERY_MAX_BUSINESS_DAYS,
  SAME_DAY_PROCESSING_CUTOFF,
  PROMO_DEMO_DISCOUNT_RATE,
  STATUS_MESSAGE_DURATION
} from './modules.js';

(function() {
  'use strict';

  // Detect SDK mode (running inside iframe with FyncallApp or in direct loading mode)
  // SDK mode is detected when:
  // 1. window.FyncallApp exists (set by embed.html or shim's direct loading)
  // 2. OR we're inside an iframe (embed.html scenario)
  // 3. OR #fyncall-root element exists (direct loading scenario)
  const isInIframe = window.parent !== window;
  const hasFyncallApp = !!(window.FyncallApp);
  const hasFyncallRoot = !!document.getElementById('fyncall-root');
  const isSDKMode = hasFyncallApp || isInIframe || hasFyncallRoot;

  console.log('[FyncallWidget] SDK Mode:', isSDKMode, 'In iframe:', isInIframe, 'Has FyncallApp:', hasFyncallApp, 'Has fyncall-root:', hasFyncallRoot);

  // =========================================================================
  // FYNCALL WIDGET SDK
  // =========================================================================
  // A comprehensive chat widget with support for:
  // - Real-time messaging via WebSocket
  // - Shopify integration (cart, customer detection)
  // - Proactive engagement triggers
  // - Rich message types (products, orders, comparisons)
  // - Pre-chat forms and CSAT surveys
  // - Theme customization (light/dark mode)
  // =========================================================================

  const FyncallWidgetSDK = {
    // =====================================================================
    // STATE PROPERTIES
    // =====================================================================
    config: null,
    tenantConfig: null,
    session: null,
    visitorToken: null,
    isOpen: isSDKMode,
    hasConversation: false,
    sessionCheckComplete: false,
    elements: {},
    reconnectAttempts: 0,
    messageReactions: {},
    unreadCount: 0,
    lastActivity: Date.now(),
    proactiveShown: false,
    cartCount: 0,
    appliedPromoCode: null,
    appliedPromoDiscount: 0,
    shopifyContext: null,
    isSDKMode: isSDKMode,

    // Proactive engagement state
    proactiveState: {
      triggersShown: {},      // Track which trigger IDs have been shown
      triggerCount: 0,        // Total triggers shown this session
      lastTriggerTime: 0,     // Timestamp of last trigger
      scrollDepth: 0,         // Current scroll depth percentage
      pageLoadTime: Date.now(),  // When the page was loaded
      exitIntentBound: false, // Whether exit intent listener is bound
      scrollListenerBound: false,
    },

    // =====================================================================
    // STATE SYNCHRONIZATION
    // =====================================================================

    /**
     * Sync local state with centralized state module
     * Call this after any state changes to ensure consistency
     */
    _syncState: function() {
      setModuleSession(this.session);
      setModuleVisitorToken(this.visitorToken);
      setModuleWidgetOpen(this.isOpen);
    },

    // =====================================================================
    // DEBUG / DEVELOPMENT HELPERS
    // =====================================================================

    /**
     * Check if running in local development mode
     * @returns {boolean} True if localhost, 127.0.0.1, or ngrok URL
     */
    isLocalDev: function() {
      const hostname = window.location.hostname.toLowerCase();
      const baseUrl = (this.config?.baseUrl || '').toLowerCase();

      return (
        hostname === 'localhost' ||
        hostname === '127.0.0.1' ||
        hostname.includes('ngrok') ||
        baseUrl.includes('localhost') ||
        baseUrl.includes('127.0.0.1') ||
        baseUrl.includes('ngrok')
      );
    },

    /**
     * Create debug reset button for local development
     * Only shows when running locally (localhost, 127.0.0.1, ngrok)
     * @param {HTMLElement} container - Widget container element
     */
    createDebugResetButton: function(container) {
      if (!this.isLocalDev()) {
        return; // Only show in local development
      }

      const resetButton = document.createElement('button');
      resetButton.id = 'fyncall-debug-reset';
      resetButton.innerHTML = '🔄';
      resetButton.title = 'Reset Session (Dev Only)';
      resetButton.style.cssText = `
                position: fixed;
                bottom: 100px;
                right: 25px;
                width: 32px;
                height: 32px;
                border-radius: 50%;
                background: #ff6b6b;
                color: white;
                border: none;
                cursor: pointer;
                font-size: 14px;
                z-index: 2147483646;
                box-shadow: 0 2px 8px rgba(0,0,0,0.2);
                display: flex;
                align-items: center;
                justify-content: center;
                opacity: 0.7;
                transition: opacity 0.2s, transform 0.2s;
            `;

      resetButton.addEventListener('mouseenter', () => {
        resetButton.style.opacity = '1';
        resetButton.style.transform = 'scale(1.1)';
      });

      resetButton.addEventListener('mouseleave', () => {
        resetButton.style.opacity = '0.7';
        resetButton.style.transform = 'scale(1)';
      });

      resetButton.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (confirm('Reset widget session? This will clear all session data and reload.')) {
          console.log('[FyncallWidget] Debug: Resetting session...');

          // Clear all session-related localStorage
          const keysToRemove = [];
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && (
              key.startsWith('fynchat_') ||
              key.startsWith('fyncall_') ||
              key.includes('visitor') ||
              key.includes('session')
            )) {
              keysToRemove.push(key);
            }
          }
          keysToRemove.forEach(key => localStorage.removeItem(key));

          // Clear IndexedDB if available
          try {
            const databases = await indexedDB.databases();
            for (const db of databases) {
              if (db.name && db.name.includes('fyncall')) {
                indexedDB.deleteDatabase(db.name);
              }
            }
          } catch (err) {
            console.warn('[FyncallWidget] Could not clear IndexedDB:', err);
          }

          console.log('[FyncallWidget] Session data cleared, reloading...');
          window.location.reload();
        }
      });

      // Append to body (outside shadow DOM for visibility)
      document.body.appendChild(resetButton);
      console.log('[FyncallWidget] Debug reset button added (local dev mode)');
    },

    // =====================================================================
    // INITIALIZATION
    // =====================================================================

    init: async function(config) {
      try {
        // Prevent duplicate initialization
        if (window._fyncallWidgetInitialized) {
          console.warn('[FyncallWidget] Widget already initialized, skipping duplicate init');
          return this;
        }
        window._fyncallWidgetInitialized = true;

        console.log('[FyncallWidget] Initializing with config:', config);

        // In SDK mode, get config from FyncallApp
        if (this.isSDKMode && window.FyncallApp) {
          config = config || {};
          config.token = config.token || window.FyncallApp.token;
          config.widgetId = config.widgetId || window.FyncallApp.widgetId;
          config.baseUrl = config.baseUrl || window.FyncallApp.apiBaseUrl;
        }

        // Set base config first
        // Priority: 1) config.baseUrl, 2) injected __FYNCALL_ENV__, 3) fallback
        const envConfig = window.__FYNCALL_ENV__ || {};
        this.config = {
          baseUrl: config.baseUrl || envConfig.apiUrl || 'https://api.fyncall.com',
          showLauncher: config.showLauncher !== false,
          enableProactive: config.enableProactive !== false,
          proactiveDelay: config.proactiveDelay || 30000,
          enableVoice: config.enableVoice || false,
          enableReactions: config.enableReactions !== false,
          ...config
        };

        // Initialize centralized config module BEFORE fetching tenant config
        initConfig({
          baseUrl: this.config.baseUrl,
          ...this.config
        });
        setModuleSDKMode(this.isSDKMode);

        // Initialize auth module with token (token-based auth only)
        // Token and related data are passed from Fyncall global object (set by loader)
        const F = window.Fyncall || {};
        _initAuth({
          token: F._token || config.token,
          tokenExpiresAt: F._tokenExpiresAt || config.tokenExpiresAt,
          widgetId: F._widgetId || config.widgetId
        });

        // Fetch tenant configuration from server
        await this.fetchTenantConfig();

        // Apply server config to widget config
        if (this.tenantConfig) {
          const appearance = this.tenantConfig.appearance || {};
          const behavior = this.tenantConfig.behavior || {};
          const messages = this.tenantConfig.messages || {};
          const offline = this.tenantConfig.offline || {};
          const proactive = this.tenantConfig.proactive || {};

          this.config = {
            ...this.config,
            // Theme settings (position is always bottom-right, hardcoded in CSS)
            primaryColor: appearance.primary_color || this.config.primaryColor || '#007bff',
            secondaryColor: appearance.secondary_color || '#6c757d',
            textColor: appearance.text_color || '#ffffff',
            backgroundColor: appearance.background_color || '#ffffff',
            borderRadius: appearance.border_radius || '12px',
            widgetSize: appearance.widget_size || 'medium',
            // Appearance settings
            title: appearance.title || this.config.title || 'Chat with us',
            subtitle: appearance.subtitle || this.config.subtitle || 'Usually replies in minutes',
            // Header title settings
            useCompanyNameAsHeader: appearance.use_company_name_as_header !== false,
            headerTitle: appearance.header_title || 'Support Team',
            showAgentAvatar: appearance.show_agent_avatar !== false,
            agentAvatarUrl: appearance.agent_avatar_url || null,
            showTypingIndicator: appearance.show_typing_indicator !== false,
            enableFileUpload: appearance.show_file_upload !== false,
            // Behavior settings
            autoOpen: behavior.auto_open || false,
            autoOpenDelay: behavior.auto_open_delay || 3000,
            soundNotifications: behavior.sound_notifications !== false,
            // Message settings
            welcomeMessage: appearance.welcome_message || messages.welcome_message || 'Welcome! How can we help you today?',
            inputPlaceholder: appearance.input_placeholder || messages.input_placeholder || 'Type your message...',
            // Home screen settings
            companyLogo: appearance.company_logo_url || null,
            companyName: appearance.company_name || '',
            homeScreenEnabled: this.tenantConfig.quick_replies?.home_screen_enabled !== false,
            // Debug: log home screen setting extraction
            _debug_homeScreenRaw: this.tenantConfig.quick_replies?.home_screen_enabled,
            welcomeTitle: this.tenantConfig.quick_replies?.home_greeting_title || messages.greeting_message || 'Hi there! 👋',
            welcomeSubtitle: this.tenantConfig.quick_replies?.home_greeting_subtitle || messages.welcome_message || 'How can we help you today?',
            startChatText: this.tenantConfig.quick_replies?.home_start_chat_text || 'Start a conversation',
            homeShowQuickActions: this.tenantConfig.quick_replies?.home_show_quick_actions !== false,
            homeShowFaqs: this.tenantConfig.quick_replies?.home_show_faqs !== false,
            agentName: appearance.agent_name || 'Support Team',
            // Powered by settings
            showPoweredBy: appearance.show_powered_by !== false,
            poweredByText: appearance.powered_by_text || 'Powered by Fyncall',
            // Phase 2: Launcher settings
            launcherStyle: appearance.launcher_style || 'circle',
            launcherSize: appearance.launcher_size || 'medium',
            launcherAnimation: appearance.launcher_animation || 'none',
            launcherText: appearance.launcher_text || null,
            launcherIcon: appearance.launcher_icon || 'chat',
            customLauncherIconUrl: appearance.custom_launcher_icon_url || null,
            launcherPositionOffset: appearance.launcher_position_offset || { x: 20, y: 20 },
            // Header enhancements (default to false, only show when explicitly enabled)
            showSoundToggle: appearance.show_sound_toggle === true,
            showDarkModeToggle: appearance.show_dark_mode_toggle === true,
            // Message features
            showReadReceipts: appearance.show_read_receipts !== false,
            messageAnimations: appearance.message_animations !== false,
            showLinkPreviews: appearance.show_link_previews !== false,
            // Notification badge
            showNotificationBadge: appearance.show_notification_badge !== false,
            // Transcript download (default to false, only show when explicitly enabled)
            transcriptEnabled: appearance.transcript_enabled === true,
            // Phase 2: Quick Replies
            quickReplies: this.tenantConfig.quick_replies || {},
            // Phase 2: Message Styling
            messageStyling: this.tenantConfig.message_styling || {},
            // Offline mode
            offlineEnabled: offline.enabled !== false,
            offlineTitle: offline.title || "We're away",
            offlineMessage: offline.message || "Leave us a message and we'll get back to you soon.",
            offlineEmailEnabled: offline.email_enabled !== false,
            // Proactive settings
            proactiveEnabled: proactive.enabled === true,
            proactiveDelay: proactive.default_delay || 30000,
            proactiveMessage: proactive.proactive_message || "👋 Need any help? Feel free to ask!",
            // Visual enhancements
            gradientEnabled: appearance.gradient_enabled === true,
            gradientColors: appearance.gradient_colors || ['#007bff', '#6610f2'],
            headerStyle: appearance.header_style || 'solid',
            messageBubbleStyle: appearance.message_bubble_style || 'rounded',
            shadowIntensity: appearance.shadow_intensity || 'medium',
            animationStyle: appearance.animation_style || 'subtle',
            // Widget size customization
            widgetWidth: appearance.widget_width || null,
            widgetHeight: appearance.widget_height || null,
            // Home screen header curve style: 'none', 'wave', 'rounded'
            headerCurveStyle: appearance.header_curve_style || 'wave',
            // Emoji picker
            showEmojiPicker: appearance.show_emoji_picker !== false,
          };
          console.log('[FyncallWidget] Applied server config:', this.tenantConfig);
          console.log('[FyncallWidget] Launcher settings:', {
            launcherIcon: this.config.launcherIcon,
            launcherStyle: this.config.launcherStyle,
            launcherSize: this.config.launcherSize,
            showNotificationBadge: this.config.showNotificationBadge
          });
          console.log('[FyncallWidget] Home screen settings:', {
            homeScreenEnabled: this.config.homeScreenEnabled,
            rawValue: this.tenantConfig.quick_replies?.home_screen_enabled,
            quickReplies: this.tenantConfig.quick_replies
          });
          console.log('[FyncallWidget] Header settings:', {
            useCompanyNameAsHeader: this.config.useCompanyNameAsHeader,
            companyName: this.config.companyName,
            headerTitle: this.config.headerTitle
          });
          console.log('[FyncallWidget] Message settings:', {
            inputPlaceholder: this.config.inputPlaceholder,
            welcomeMessage: this.config.welcomeMessage,
            rawInputPlaceholder: appearance.input_placeholder,
            rawWelcomeMessage: appearance.welcome_message
          });

          // Update centralized config with tenant settings
          setTenantConfig(this.tenantConfig);
        }

        this.createWidget();
        this.bindEvents();
        this.setupProactiveEngagement();
        this.updateCartBadge();

        // Update header to ensure correct company name/avatar is displayed
        this.updateHeader();

        // Update input placeholder dynamically (in case template used default)
        if (this.elements.input && this.config.inputPlaceholder) {
          this.elements.input.placeholder = this.config.inputPlaceholder;
        }

        // Initialize desktop notifications if enabled
        initDesktopNotifications();

        // Initialize IndexedDB for message caching
        try {
          await initMessageDB();
          // Clean up old messages (older than 24 hours)
          await clearOldMessages(SESSION_TTL);
          console.log('[FyncallWidget] IndexedDB message cache initialized');
        } catch (error) {
          console.warn('[FyncallWidget] IndexedDB init failed, will use localStorage fallback:', error);
        }

        // Check for existing session with messages (async, non-blocking)
        this.checkExistingSession();

        // Handle auto-open behavior
        if (this.config.autoOpen) {
          setTimeout(() => this.open(), this.config.autoOpenDelay);
        }

        // Detect Shopify context early for customer identification
        this.detectShopifyCustomer();
        if (this.shopifyContext?.customer?.email) {
          console.log('[FyncallWidget] Logged-in Shopify customer detected:', this.shopifyContext.customer.email);
        }

        console.log('[FyncallWidget] Widget mounted successfully');

        // Listen for open/close events from shim (SDK mode)
        if (this.isSDKMode && window.FyncallApp) {
          window.FyncallApp.on('opened', () => {
            console.log('[FyncallWidget] Received opened event from shim');
            this.onWidgetOpened();
          });
          window.FyncallApp.on('closed', () => {
            console.log('[FyncallWidget] Received closed event from shim');
            this.onWidgetClosed();
          });

          // Check if widget is already open (shim may have opened before we initialized)
          // The shim sets FyncallApp.isWidgetOpen = true when it opens
          if (window.FyncallApp.isWidgetOpen) {
            console.log('[FyncallWidget] Widget already open on init, calling onWidgetOpened');
            this.onWidgetOpened();
          }
        }

        // Notify parent that we're ready (SDK mode)
        if (this.isSDKMode && window.FyncallApp && window.FyncallApp.setReady) {
          window.FyncallApp.setReady();
        }

        return this;

      } catch (error) {
        console.error('[FyncallWidget] Init failed:', error);
        throw error;
      }
    },

    fetchTenantConfig: async function() {
      // In token-based auth, tenant config comes from token exchange response
      // which is stored in Fyncall._config by the loader
      const F = window.Fyncall || {};
      if (F._config) {
        this.tenantConfig = F._config;
        console.log('[FyncallWidget] Tenant config loaded from token exchange:', this.tenantConfig);
      } else if (this.config.tenantConfig) {
        // Fallback: config passed directly (SDK mode)
        this.tenantConfig = this.config.tenantConfig;
        console.log('[FyncallWidget] Tenant config loaded from direct config:', this.tenantConfig);
      } else {
        console.warn('[FyncallWidget] No tenant config available, using defaults');
      }
    },

    // =====================================================================
    // API HELPERS
    // =====================================================================

    getHeaders: function() {
      return _getAuthHeaders();
    },

    getLauncherIcon: function() {
      const iconType = this.config.launcherIcon || 'chat';

      // Custom icon URL
      if (iconType === 'custom' && this.config.customLauncherIconUrl) {
        return `<img src="${this.config.customLauncherIconUrl}" alt="Chat" />`;
      }

      // Use imported LAUNCHER_ICONS
      return LAUNCHER_ICONS[iconType] || LAUNCHER_ICONS.chat;
    },

    // =====================================================================
    // WIDGET CREATION & DOM
    // =====================================================================

    createWidget: function() {
      const container = document.createElement('div');
      container.id = 'fyncall-widget-container';
      // Get dark mode colors from config
      const darkColors = this.tenantConfig?.appearance?.dark_mode_colors || {};

      // Generate widget HTML using template module
      container.innerHTML = generateWidgetHTML(
        this.config,
        darkColors,
        this.isSDKMode,
        () => this.getLauncherIcon()
      );

      // In SDK mode, append to #fyncall-root if it exists, otherwise body
      this.targetElement = this.isSDKMode
        ? (document.getElementById('fyncall-root') || document.body)
        : document.body;
      this.targetElement.appendChild(container);

      // Query all widget elements using template module
      this.elements = _queryWidgetElements(container);

      // Initialize CSAT state
      this.csatState = {
        rating: null,
        type: 'stars', // stars, thumbs, nps, csat
        submitted: false
      };

      // Flag to track if we're in the process of ending a conversation
      this.pendingEndConversation = false;

      // Initialize cart data
      this.cartData = { items: [], total: 0 };
      // Track products added to cart during this session (by product_id or variant_id)
      this.addedToCartIds = new Set();
      this.currentVariantProduct = null;
      this.selectedVariant = null;
      this.selectedQuantity = 1;

      // Add debug reset button in local development mode
      this.createDebugResetButton(container);

      // Initialize theme
      this.initializeTheme();

      // Initialize Phase 2 features
      this.initializePhase2Features();
    },

    /**
     * Initialize Phase 2 features: Home Screen Actions, FAQs, Message Styling
     */
    initializePhase2Features: function() {
      const quickRepliesConfig = this.config.quickReplies || {};
      const messageStylingConfig = this.config.messageStyling || {};

      // Render FAQs on home screen
      // Respect both home_screen_enabled and home_show_faqs flags
      const showFaqs = quickRepliesConfig.home_screen_enabled !== false &&
        quickRepliesConfig.home_show_faqs !== false;

      // Use configured FAQs or fall back to defaults
      // Check both correct key and legacy typo key for backwards compatibility
      const configuredFaqs = quickRepliesConfig.home_screen_faqs || quickRepliesConfig.home_scre_faqs;
      const faqs = configuredFaqs?.length
        ? configuredFaqs
        : this.getDefaultFaqs();

      if (showFaqs && faqs.length > 0) {
        this.renderHomeFaqs(faqs);
      } else {
        // Hide FAQs section if disabled
        if (this.elements.homeFaqs) this.elements.homeFaqs.style.display = 'none';
      }

      // Apply message bubble style (use appearance config with fallback to message styling)
      const bubbleStyle = this.config.messageBubbleStyle || messageStylingConfig.message_bubble_style || 'rounded';
      this.elements.container.dataset.bubbleStyle = bubbleStyle;

      // Store message styling config for reactions
      this.messageStylingConfig = messageStylingConfig;

      console.log('[FyncallWidget] Phase 2 features initialized');
    },

    /**
     * Get default FAQs for home screen
     */
    getDefaultFaqs: function() {
      return [
        { question: 'What are your business hours?', enabled: true },
        { question: 'How can I track my order?', enabled: true },
        { question: 'What is your return policy?', enabled: true }
      ];
    },

    /**
     * Render FAQs on home screen
     */
    renderHomeFaqs: function(faqs) {
      const container = this.elements.homeFaqs;
      if (!container) return;

      container.innerHTML = '';
      const enabledFaqs = faqs.filter(faq => faq.enabled !== false);

      if (enabledFaqs.length === 0) {
        container.style.display = 'none';
        return;
      }

      container.style.display = 'block';

      enabledFaqs.forEach(faq => {
        const btn = document.createElement('button');
        btn.className = 'fyncall-faq-item';
        btn.setAttribute('data-question', faq.question);
        btn.innerHTML = `
                    <span class="fyncall-faq-text">${faq.question}</span>
                    <svg class="fyncall-faq-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                `;
        btn.onclick = () => this.showChatView(faq.question);
        container.appendChild(btn);
      });
    },

    /**
     * Render home screen actions as horizontal scroll
     */
    renderHomeActions: function(actions) {
      const container = this.elements.homeActions;
      if (!container) return;

      container.innerHTML = '';
      const enabledActions = actions.filter(action => action.enabled !== false);

      if (enabledActions.length === 0) {
        container.style.display = 'none';
        if (this.elements.homeActionsLabel) this.elements.homeActionsLabel.style.display = 'none';
        return;
      }

      // Show section label
      if (this.elements.homeActionsLabel) this.elements.homeActionsLabel.style.display = 'block';
      container.style.display = 'flex';

      enabledActions.forEach(action => {
        const btn = document.createElement('button');
        btn.className = 'fyncall-action-card';
        btn.innerHTML = `
                    <div class="fyncall-action-icon">${action.icon || '📌'}</div>
                    <span class="fyncall-action-text">${action.label || action.text || 'Action'}</span>
                `;
        btn.onclick = () => this.showChatView(action.action);
        container.appendChild(btn);
      });
    },


    // =====================================================================
    // THEME MANAGEMENT - Delegates to theme.js module
    // =====================================================================
    initializeTheme: function() {
      this.currentTheme = _initTheme(this.elements.container, this.tenantConfig);
    },

    setTheme: function(theme) {
      this.currentTheme = _setTheme(theme);
    },

    getTheme: function() {
      return _getTheme();
    },

    toggleTheme: function() {
      this.currentTheme = _toggleTheme();
      return this.currentTheme;
    },


    // =====================================================================
    // EVENT BINDING
    // =====================================================================
    // Bind all widget events - delegates to event-bindings module
    bindEvents: function() {
      _bindAllEvents(this.elements, this, this.isSDKMode);
    },


    // =====================================================================
    // WIDGET STATE (OPEN/CLOSE)
    // =====================================================================
    toggle: function() { this.isOpen ? this.close() : this.open(); },

    open: function() {
      this.isOpen = true;
      this._syncState();
      this.elements.chatWindow.classList.add('open');
      if (this.elements.launcher) this.elements.launcher.style.display = 'none';
      this.hideProactive();
      this.unreadCount = 0;
      this.updateBadge();

      // Resume audio context on user interaction (for sound notifications)
      _resumeAudioContext();

      // Refresh cart data from Shopify to ensure we have latest state
      this.refreshCartData();

      // Mark widget as focused for desktop notifications
      setWidgetFocused(true);

      // Add blur background effect if enabled
      if (this.config.blurBackground && this.elements.container) {
        this.elements.container.classList.add('fyncall-blur-active');
      }

      // Show chat view if:
      // 1. There's an active conversation with messages, OR
      // 2. Home screen is disabled in configuration
      // Otherwise show home screen for new visitors
      console.log('[FyncallWidget] Open check:', {
        hasConversation: this.hasConversation,
        homeScreenEnabled: this.config.homeScreenEnabled,
        shouldShowChat: this.hasConversation || !this.config.homeScreenEnabled
      });
      if (this.hasConversation || !this.config.homeScreenEnabled) {
        console.log('[FyncallWidget] Opening to chat view - ' +
          (this.hasConversation ? 'existing conversation' : 'home screen disabled'));
        this.showChatView();
      } else {
        console.log('[FyncallWidget] Opening to home screen - no conversation');
        this.showHomeScreen();
      }
    },

    close: function() {
      this.isOpen = false;
      this._syncState();
      this.elements.chatWindow.classList.remove('open');
      if (this.elements.launcher) this.elements.launcher.style.display = 'flex';

      // Mark widget as not focused for desktop notifications
      setWidgetFocused(false);

      // Remove blur background effect
      if (this.elements.container) {
        this.elements.container.classList.remove('fyncall-blur-active');
      }
    },

    /**
     * Called when shim emits 'opened' event (SDK mode)
     * Handles view switching based on homeScreenEnabled config
     */
    onWidgetOpened: function() {
      this.isOpen = true;
      this._syncState();

      // Hide proactive message
      this.hideProactive();
      this.unreadCount = 0;
      this.updateBadge();

      // Refresh cart data from Shopify to ensure we have latest state
      this.refreshCartData();

      // Mark widget as focused for desktop notifications
      setWidgetFocused(true);

      // Show chat view if:
      // 1. There's an active conversation with messages, OR
      // 2. Home screen is disabled in configuration
      // Otherwise show home screen for new visitors
      console.log('[FyncallWidget] onWidgetOpened check:', {
        hasConversation: this.hasConversation,
        homeScreenEnabled: this.config.homeScreenEnabled,
        shouldShowChat: this.hasConversation || !this.config.homeScreenEnabled
      });
      if (this.hasConversation || !this.config.homeScreenEnabled) {
        console.log('[FyncallWidget] SDK mode: Opening to chat view - ' +
          (this.hasConversation ? 'existing conversation' : 'home screen disabled'));
        this.showChatView();
      } else {
        console.log('[FyncallWidget] SDK mode: Opening to home screen - no conversation');
        this.showHomeScreen();
      }
    },

    /**
     * Called when shim emits 'closed' event (SDK mode)
     */
    onWidgetClosed: function() {
      this.isOpen = false;
      this._syncState();

      // Mark widget as not focused for desktop notifications
      setWidgetFocused(false);
    },

    // Toggle sound notifications
    toggleSound: function() {
      this.config.soundNotifications = !this.config.soundNotifications;
      const btn = document.getElementById('fyncall-sound-toggle');
      if (btn) {
        // Update icon and label based on state
        btn.innerHTML = this.config.soundNotifications
          ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 5L6 9H2v6h4l5 4V5z"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/></svg><span>Sound on</span>'
          : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 5L6 9H2v6h4l5 4V5z"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg><span>Sound off</span>';
        btn.title = this.config.soundNotifications ? 'Mute sound' : 'Unmute sound';
      }
      // Save preference to localStorage - uses imported module
      saveSoundEnabled(this.config.soundNotifications);
      console.log('[FyncallWidget] Sound notifications:', this.config.soundNotifications ? 'enabled' : 'disabled');
    },

    // Toggle dark mode
    toggleDarkMode: function() {
      const container = document.getElementById('fyncall-widget-container');
      if (!container) return;

      const currentTheme = container.getAttribute('data-theme');
      const newTheme = currentTheme === 'dark' ? 'light' : 'dark';
      container.setAttribute('data-theme', newTheme);

      const btn = document.getElementById('fyncall-darkmode-toggle');
      if (btn) {
        btn.innerHTML = newTheme === 'dark'
          ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>'
          : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>';
        btn.title = newTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode';
      }
      // Save preference to localStorage - uses imported module
      saveTheme(newTheme);
    },

    // Download chat transcript as text file (legacy - kept for backward compatibility)
    downloadTranscript: function() {
      _downloadTranscriptText(this.elements.messages, this.config, (msg, type) => this.showStatus(msg, type));
    },

    showHomeScreen: function() {
      this.elements.homeScreen.classList.add('visible');
      this.elements.homeScreen.classList.remove('hidden');
      this.elements.chatView.classList.remove('active');
    },

    showChatView: function(initialMessage) {
      this.elements.homeScreen.classList.remove('visible');
      this.elements.homeScreen.classList.add('hidden');
      this.elements.chatView.classList.add('active');

      // Create session if not exists
      if (!this.session) {
        if (this.shouldShowPrechatForm()) {
          this.showPrechatForm();
          // Store the initial message to send after session creation
          if (initialMessage) {
            this.pendingMessage = initialMessage;
          }
        } else {
          this.createSession().then(() => {
            if (initialMessage) {
              this.elements.input.value = initialMessage;
              this.sendMessage();
            }
          });
        }
      } else if (initialMessage) {
        // Session exists, send the message directly
        this.elements.input.value = initialMessage;
        this.sendMessage();
      }
    },

    /**
     * Toggle header menu dropdown
     */
    toggleHeaderMenu: function() {
      if (this.elements.menuDropdown) {
        this.elements.menuDropdown.classList.toggle('show');
      }
    },

    /**
     * Close header menu dropdown
     */
    closeHeaderMenu: function() {
      if (this.elements.menuDropdown) {
        this.elements.menuDropdown.classList.remove('show');
      }
    },

    /**
     * Update the chat header with current config values
     * This ensures the header displays the correct company name, avatar, and subtitle
     */
    updateHeader: function() {
      const container = this.elements.container;
      if (!container) return;

      const headerAvatar = container.querySelector('#fyncall-header-avatar');
      const headerText = container.querySelector('#fyncall-header-text');

      if (!headerText) return;

      // Get current config values
      const companyName = this.config.companyName || '';
      const companyLogo = this.config.companyLogo;
      const agentAvatarUrl = this.config.agentAvatarUrl;
      const useCompanyNameAsHeader = this.config.useCompanyNameAsHeader !== false;
      const headerTitle = this.config.headerTitle || 'Support Team';
      const subtitle = this.config.subtitle || 'Usually replies in minutes';

      // Determine display title
      const displayTitle = useCompanyNameAsHeader
        ? (companyName || 'Chat with us')
        : headerTitle;

      // Update title
      const titleEl = headerText.querySelector('h3');
      if (titleEl) {
        titleEl.textContent = displayTitle;
      }

      // Update subtitle
      const subtitleEl = headerText.querySelector('p');
      if (subtitleEl) {
        // Preserve the online dot
        subtitleEl.innerHTML = `<span class="fyncall-online-dot"></span>${subtitle}`;
      }

      // Update avatar
      if (headerAvatar) {
        const companyInitials = (companyName || 'FY').substring(0, 2).toUpperCase();
        if (companyLogo) {
          headerAvatar.innerHTML = `<img src="${companyLogo}" alt="" onerror="this.parentElement.innerHTML='${companyInitials}'">`;
        } else if (agentAvatarUrl) {
          headerAvatar.innerHTML = `<img src="${agentAvatarUrl}" alt="" onerror="this.parentElement.innerHTML='${companyInitials}'">`;
        } else {
          headerAvatar.innerHTML = companyInitials;
        }
      }

      console.log('[FyncallWidget] Header updated:', { displayTitle, companyName, useCompanyNameAsHeader });
    },

    /**
     * End the current conversation and return to home screen
     * This clears the session and allows the user to start fresh
     */
    endConversation: function() {
      console.log('[FyncallWidget] Ending conversation...');

      // Store messages for transcript before clearing
      this.pendingTranscriptMessages = this.collectMessagesForTranscript();

      // If transcript is enabled and we have messages, show end chat card in chat area
      if (this.config.transcriptEnabled && this.pendingTranscriptMessages.length > 0) {
        this.showEndChatCard();
      } else {
        // No transcript option, directly finalize
        this.finalizeEndConversation();
      }
    },

    /**
     * Collect messages for transcript before clearing
     */
    collectMessagesForTranscript: function() {
      return _collectMessagesForTranscript(this.elements.messages, this.config);
    },

    /**
     * Show end chat card in the chat area with download transcript option
     */
    showEndChatCard: function() {
      const endChatCard = document.createElement('div');
      endChatCard.className = 'fyncall-end-chat-card';
      endChatCard.innerHTML = `
                <div class="fyncall-end-chat-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                        <polyline points="22 4 12 14.01 9 11.01"/>
                    </svg>
                </div>
                <h4>Chat ended</h4>
                <p>Would you like to download a transcript of this conversation?</p>
                <div class="fyncall-end-chat-actions">
                    <button class="fyncall-end-chat-btn primary" id="fyncall-download-transcript-btn">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                            <polyline points="7 10 12 15 17 10"/>
                            <line x1="12" y1="15" x2="12" y2="3"/>
                        </svg>
                        Download Transcript
                    </button>
                    <button class="fyncall-end-chat-btn secondary" id="fyncall-skip-transcript-btn">
                        No thanks
                    </button>
                </div>
            `;

      this.elements.messages.appendChild(endChatCard);
      this.scrollToBottom();

      // Bind button events
      const downloadBtn = endChatCard.querySelector('#fyncall-download-transcript-btn');
      const skipBtn = endChatCard.querySelector('#fyncall-skip-transcript-btn');

      downloadBtn.onclick = () => {
        this.downloadTranscriptAndEnd();
      };

      skipBtn.onclick = () => {
        this.finalizeEndConversation();
      };
    },

    /**
     * Download transcript then finalize end conversation
     */
    downloadTranscriptAndEnd: function() {
      if (this.pendingTranscriptMessages && this.pendingTranscriptMessages.length > 0) {
        this.downloadTranscript();
      }
      this.finalizeEndConversation();
    },

    /**
     * Show the end chat modal (legacy - kept for compatibility)
     */
    showEndChatModal: function() {
      if (this.elements.endChatModal) {
        this.elements.endChatModal.classList.add('show');
      }
    },

    /**
     * Close the end chat modal
     */
    closeEndChatModal: function() {
      if (this.elements.endChatModal) {
        this.elements.endChatModal.classList.remove('show');
      }
    },

    /**
     * Finalize ending the conversation (clear session, show home)
     * If postchat survey is enabled, show it first before clearing
     */
    finalizeEndConversation: function() {
      // Check if postchat survey should be shown
      const formsConfig = this.tenantConfig?.forms;
      if (formsConfig?.postchat_enabled && !this.csatState.submitted) {
        console.log('[FyncallWidget] Showing CSAT survey before ending conversation');
        this.pendingEndConversation = true;
        this.showCsatSurvey();
        return; // Wait for survey to close before clearing session
      }

      // No survey or already submitted - proceed with cleanup
      this.completeEndConversation();
    },

    /**
     * Complete the end conversation process (called after survey or directly)
     */
    completeEndConversation: function() {
      // Close WebSocket connection using module
      _disconnectWebSocket(true);

      // Clear current session state
      this.session = null;
      this.visitorToken = null;
      this.clearSavedSession();
      resetWebSocketSession();

      // Clear messages from UI
      this.elements.messages.innerHTML = `
                <div class="fyncall-typing">
                    <div class="fyncall-typing-dots"><span></span><span></span><span></span></div>
                    <span class="fyncall-typing-text">Luna is typing...</span>
                </div>
            `;

      // Re-query the typing element after replacing innerHTML (old reference is stale)
      this.elements.typing = this.elements.messages.querySelector('.fyncall-typing');

      // Reset reconnect attempts
      this.reconnectAttempts = 0;
      this.sessionInvalid = false;

      // Clear pending transcript messages
      this.pendingTranscriptMessages = null;

      // Reset pending end conversation flag
      this.pendingEndConversation = false;

      // Show home screen
      this.showHomeScreen();

      console.log('[FyncallWidget] Conversation ended, showing home screen');
    },

    /**
     * Download transcript as a beautiful PDF
     */
    downloadTranscriptPDF: function() {
      _downloadTranscriptPDF(
        this.pendingTranscriptMessages,
        this.elements.messages,
        this.config,
        (msg, type) => this.showStatus(msg, type)
      );
    },

    // Status message - delegates to toast.js module
    showStatus: function(message, type) {
      _showStatus(message, type, this.elements.status, type === 'success' ? STATUS_MESSAGE_DURATION : 0);
    },

    updateBadge: function() {
      // In SDK mode, notify parent of unread count change
      if (this.isSDKMode && window.FyncallApp && window.FyncallApp.setUnreadCount) {
        window.FyncallApp.setUnreadCount(this.unreadCount);
      }

      // Only show badge if notification badge is enabled in config (not in SDK mode)
      if (this.isSDKMode || this.config.showNotificationBadge === false) {
        if (this.elements.badge) this.elements.badge.classList.remove('show');
        return;
      }
      if (this.elements.badge) {
        this.elements.badge.textContent = this.unreadCount;
        this.elements.badge.classList.toggle('show', this.unreadCount > 0);
      }
    },

    hideProactive: function() {
      if (this.elements.proactive) this.elements.proactive.classList.remove('show');
    },

    showProactive: function() {
      // Don't show proactive in SDK mode (shim handles it)
      if (this.isSDKMode) return;

      // Check both legacy and new config settings
      const proactiveEnabled = this.config.proactiveEnabled || this.config.enableProactive;
      if (!this.isOpen && !this.proactiveShown && proactiveEnabled && this.elements.proactive) {
        this.elements.proactive.classList.add('show');
        this.proactiveShown = true;
      }
    },


    // =====================================================================
    // PROACTIVE ENGAGEMENT (delegated to module)
    // =====================================================================
    setupProactiveEngagement: function() {
      _setupProactiveEngagement({
        onShowMessage: (message, trigger) => this.showProactiveWithMessage(message),
        onOpenWidget: () => this.open(),
        getCartValue: () => {
          const cartTotal = this.shopifyContext?.cart?.total_price;
          return cartTotal ? cartTotal / 100 : 0;
        },
        detectShopifyContext: () => {
          this.detectShopifyCustomer();
          return this.shopifyContext;
        }
      }, this.config, this.tenantConfig);
    },

    // Sync local proactive state with module (called after trigger shown)
    syncProactiveState: function() {
      const moduleState = getLocalProactiveState();
      this.proactiveState = { ...this.proactiveState, ...moduleState };
    },

    showProactiveWithMessage: function(message) {
      if (this.isOpen || this.proactiveShown || this.isSDKMode) return;

      // Update the proactive message content
      if (this.elements.proactive) {
        const messageEl = this.elements.proactive.querySelector('p');
        if (messageEl) {
          messageEl.textContent = message;
        }

        this.elements.proactive.classList.add('show');
        this.proactiveShown = true;
      }
    },


    // =====================================================================
    // SESSION MANAGEMENT
    // =====================================================================
    getVisitorId: function() {
      return getOrCreateVisitorId();
    },

    saveSession: function() {
      saveSessionToStorage(this.session, this.visitorToken);
      this._syncState();
    },

    getSavedSession: function() {
      return getSavedSession();
    },

    // Clear session - delegates to session.js module
    clearSavedSession: async function() {
      await _clearSession();
      this.hasConversation = false;
      this.session = null;
      this.visitorToken = null;
      this._syncState();
    },

    createSession: async function() {
      this.showStatus('Connecting...', 'info');

      // Check for existing session first
      const savedSession = this.getSavedSession();
      if (savedSession) {
        // Check if session has expired (24h inactivity)
        if (isSessionExpired()) {
          console.log('[FyncallWidget] Session expired after 24h inactivity, creating new session');
          await this.clearSavedSession();
        } else {
          console.log('[FyncallWidget] Found valid saved session:', savedSession.sessionId);
          const resumed = await this.resumeSession(savedSession);
          if (resumed) return;
          // If resume failed, clear saved session and create new one
          await this.clearSavedSession();
        }
      }

      try {
        const visitorId = this.getVisitorId();
        const widgetConfigId = this.config.widgetConfigId || 1;

        // Detect Shopify context and get best available visitor info
        const visitorInfo = this.getVisitorInfo();
        const shopifyContext = this.getShopifyContextForSession();

        // Build session data with Shopify context if available
        const sessionData = {
          page_url: window.location.href,
          page_title: document.title,
          referrer: document.referrer || null,
          user_agent: navigator.userAgent
        };

        // Add Shopify context if detected
        if (shopifyContext) {
          sessionData.shopify_context = shopifyContext;
          console.log('[FyncallWidget] Including Shopify context in session');
        }

        const response = await fetch(`${this.config.baseUrl}/api/v1/widget/sessions`, {
          method: 'POST',
          headers: this.getHeaders(),
          body: JSON.stringify({
            visitor_id: visitorId,
            widget_config_id: widgetConfigId,
            visitor_info: visitorInfo,
            session_data: sessionData
          })
        });
        if (!response.ok) throw new Error(`Session failed: ${response.status}`);
        const data = await response.json();
        this.session = data.session.id;
        this.visitorToken = data.session.visitor_token;
        this.saveSession();
        console.log('[FyncallWidget] Session created:', this.session);
        this.showStatus('Connected!', 'success');
        this.elements.input.disabled = false;
        this.elements.sendBtn.disabled = false;
        this.elements.input.focus();
        this.connectWebSocket();
        if (data.session && data.session.greeting_message) {
          this.addMessage(data.session.greeting_message, 'bot', null, this.getDefaultQuickReplies());
        } else {
          this.addMessage(this.config.welcomeMessage || "Hi there! 👋 How can I help you today?", 'bot', null, this.getDefaultQuickReplies());
        }
      } catch (error) {
        console.error('[FyncallWidget] Session error:', error);
        this.showStatus('Connection failed. Please refresh.', 'error');
      }
    },

    /**
     * Check for existing session on widget mount to determine if we have an active conversation
     * This runs asynchronously and sets hasConversation flag for use when widget opens
     */
    checkExistingSession: async function() {
      const savedSession = this.getSavedSession();
      if (!savedSession) {
        this.sessionCheckComplete = true;
        this.hasConversation = false;
        return;
      }

      // Check if session has expired (24h inactivity)
      if (isSessionExpired()) {
        console.log('[FyncallWidget] Pre-check - Session expired after 24h inactivity');
        this.clearSavedSession();
        this.sessionCheckComplete = true;
        this.hasConversation = false;
        return;
      }

      // Check cache first for instant result - try IndexedDB first, then localStorage
      let cachedMessages = null;
      try {
        cachedMessages = await getMessagesFromIndexedDB(savedSession.sessionId);
      } catch (error) {
        console.warn('[FyncallWidget] IndexedDB read failed, trying localStorage');
      }

      if (!cachedMessages || cachedMessages.length === 0) {
        cachedMessages = getCachedMessages(savedSession.sessionId);
      }

      if (cachedMessages && cachedMessages.length > 0) {
        this.hasConversation = true;
        this.sessionCheckComplete = true;
        console.log('[FyncallWidget] Pre-check - Has cached conversation:', true, 'Cached messages:', cachedMessages.length);
        // Still fetch from server in background to update cache
      }

      try {
        // Check if session exists and has messages
        const response = await fetch(
          `${this.config.baseUrl}/api/v1/widget/sessions/${savedSession.sessionId}/messages?visitor_token=${encodeURIComponent(savedSession.visitorToken)}`,
          { method: 'GET', headers: this.getHeaders() }
        );

        if (!response.ok) {
          // Session invalid, will create new one when needed
          // But if we have cache, still show hasConversation
          if (!cachedMessages || cachedMessages.length === 0) {
            this.hasConversation = false;
          }
          this.sessionCheckComplete = true;
          return;
        }

        const data = await response.json();
        const messages = data.data?.messages || data.messages || data || [];

        // Update cache with server messages (both localStorage and IndexedDB)
        if (messages.length > 0) {
          cacheMessages(savedSession.sessionId, messages);
          try {
            await saveMessagesToIndexedDB(savedSession.sessionId, messages);
          } catch (error) {
            console.warn('[FyncallWidget] Failed to save to IndexedDB:', error);
          }
          updateLastSync();
        }

        // Set flags based on whether there are existing messages (cache or server)
        this.hasConversation = messages.length > 0 || (cachedMessages && cachedMessages.length > 0);
        this.sessionCheckComplete = true;
        console.log('[FyncallWidget] Pre-check - Has conversation:', this.hasConversation, 'Server messages:', messages.length);
      } catch (error) {
        console.error('[FyncallWidget] Pre-check failed:', error);
        this.sessionCheckComplete = true;
        // If we have cache, still show hasConversation despite server error
        if (!cachedMessages || cachedMessages.length === 0) {
          this.hasConversation = false;
        }
      }
    },

    resumeSession: async function(savedSession) {
      // STEP 1: Load cached messages immediately for instant display
      // Try IndexedDB first (larger capacity), fall back to localStorage
      let cachedMessages = null;
      let loadedFromCache = false;

      try {
        cachedMessages = await getMessagesFromIndexedDB(savedSession.sessionId);
        if (cachedMessages && cachedMessages.length > 0) {
          console.log('[FyncallWidget] Loaded', cachedMessages.length, 'messages from IndexedDB cache');
        }
      } catch (error) {
        console.warn('[FyncallWidget] IndexedDB cache failed, trying localStorage:', error);
      }

      // Fall back to localStorage if IndexedDB didn't have messages
      if (!cachedMessages || cachedMessages.length === 0) {
        cachedMessages = getCachedMessages(savedSession.sessionId);
        if (cachedMessages && cachedMessages.length > 0) {
          console.log('[FyncallWidget] Loaded', cachedMessages.length, 'messages from localStorage cache');
        }
      }

      if (cachedMessages && cachedMessages.length > 0) {
        cachedMessages.forEach(msg => {
          const type = msg.sender_type === 'visitor' ? 'visitor' : 'bot';
          this.addMessage(msg.content, type, msg.message_metadata);
        });
        loadedFromCache = true;
        this.hasConversation = true;

        // Refresh cart data to update any cart panels with current Shopify cart state
        this.refreshCartData();
      }

      try {
        // STEP 2: Fetch session messages from server to verify session and get any new messages
        const response = await fetch(
          `${this.config.baseUrl}/api/v1/widget/sessions/${savedSession.sessionId}/messages?visitor_token=${encodeURIComponent(savedSession.visitorToken)}`,
          { method: 'GET', headers: this.getHeaders() }
        );

        // Handle any non-OK response as invalid session (including 500 errors)
        if (!response.ok) {
          console.log('[FyncallWidget] Session expired or invalid (status:', response.status, '), creating new one');
          // If we loaded from cache, still return true to show cached messages
          // but clear the saved session so a new one is created on next interaction
          if (loadedFromCache) {
            console.log('[FyncallWidget] Using cached messages despite server error');
            this.session = savedSession.sessionId;
            this.visitorToken = savedSession.visitorToken;
            this.sessionCheckComplete = true;
            // Don't connect WebSocket since session is invalid on server
            return true;
          }
          this.clearSavedSession();
          return false;
        }

        // Parse response and check for errors
        const data = await response.json();
        if (data.success === false || data.error) {
          console.log('[FyncallWidget] Session response indicates error:', data.error || data.message);
          if (loadedFromCache) {
            console.log('[FyncallWidget] Using cached messages despite server error');
            this.session = savedSession.sessionId;
            this.visitorToken = savedSession.visitorToken;
            this.sessionCheckComplete = true;
            return true;
          }
          this.clearSavedSession();
          return false;
        }

        this.session = savedSession.sessionId;
        this.visitorToken = savedSession.visitorToken;
        updateSessionActivity(); // Update activity timestamp on successful resume
        console.log('[FyncallWidget] Session resumed:', this.session);
        this.showStatus('Connected!', 'success');
        this.elements.input.disabled = false;
        this.elements.sendBtn.disabled = false;
        this.elements.input.focus();
        this.connectWebSocket();

        // STEP 3: Load server messages and update cache
        const serverMessages = data.data?.messages || data.messages || data || [];

        // Update cache with server messages (both localStorage and IndexedDB)
        if (serverMessages.length > 0) {
          cacheMessages(savedSession.sessionId, serverMessages);
          try {
            await saveMessagesToIndexedDB(savedSession.sessionId, serverMessages);
          } catch (error) {
            console.warn('[FyncallWidget] Failed to save to IndexedDB:', error);
          }
          updateLastSync();
        }

        // Set hasConversation flag
        this.hasConversation = (cachedMessages?.length > 0) || (serverMessages.length > 0);
        this.sessionCheckComplete = true;
        console.log('[FyncallWidget] Has conversation:', this.hasConversation);

        if (loadedFromCache) {
          // We already displayed cached messages, now check for any NEW messages from server
          // Compare by content + sender_type since cached messages may have temporary IDs
          const cachedSignatures = new Set(cachedMessages.map(m =>
            `${m.sender_type}:${m.content?.substring(0, 50)}`
          ));
          const newMessages = serverMessages.filter(m =>
            !cachedSignatures.has(`${m.sender_type}:${m.content?.substring(0, 50)}`)
          );

          if (newMessages.length > 0) {
            console.log('[FyncallWidget] Loading', newMessages.length, 'new messages from server');
            newMessages.forEach(msg => {
              const type = msg.sender_type === 'visitor' ? 'visitor' : 'bot';
              this.addMessage(msg.content, type, msg.message_metadata);
            });
          }
        } else if (serverMessages.length > 0) {
          // No cache, load all server messages
          console.log('[FyncallWidget] Loading', serverMessages.length, 'messages from server');
          serverMessages.forEach(msg => {
            const type = msg.sender_type === 'visitor' ? 'visitor' : 'bot';
            this.addMessage(msg.content, type, msg.message_metadata);
          });
        } else {
          // No messages yet, show greeting
          this.addMessage(this.config.welcomeMessage || "Hi there! 👋 How can I help you today?", 'bot', null, this.getDefaultQuickReplies());
        }

        return true;
      } catch (error) {
        console.error('[FyncallWidget] Failed to resume session:', error);
        // If we loaded from cache, return true to show cached messages
        if (loadedFromCache) {
          console.log('[FyncallWidget] Using cached messages despite fetch error');
          this.session = savedSession.sessionId;
          this.visitorToken = savedSession.visitorToken;
          this.sessionCheckComplete = true;
          this.hasConversation = true;
          return true;
        }
        return false;
      }
    },

    getDefaultQuickReplies: function() {
      // Default quick replies disabled - returning empty array
      return [];
    },


    // =====================================================================
    // WEBSOCKET
    // =====================================================================
    connectWebSocket: function() {
      if (!this.session || !this.visitorToken) return;

      // Use the websocketManager module for connection
      _connectWebSocket({
        sessionId: this.session,
        visitorToken: this.visitorToken,
        onInvalidSession: () => this.handleInvalidSession()
      });

      // Subscribe to WebSocket events (only once)
      if (!this._wsSubscribed) {
        onWebSocketMessage((data) => this.handleWebSocketMessage(data));
        onWebSocketStatus((status, data) => this.handleWebSocketStatus(status, data));
        this._wsSubscribed = true;
      }
    },

    handleWebSocketStatus: function(status, data) {
      switch (status) {
        case 'connected':
          this.reconnectAttempts = 0;
          break;
        case 'disconnected':
          // Handled by websocketManager's auto-reconnect
          break;
        case 'reconnecting':
          console.log(`[FyncallWidget] Reconnecting (attempt ${data.attempt})`);
          break;
        case 'max_retries':
          console.log('[FyncallWidget] Max reconnect attempts reached');
          this.validateSession();
          break;
        case 'session_invalid':
          this.handleInvalidSession();
          break;
      }
    },

    // Handle invalid/stale session - clear and reinitialize
    handleInvalidSession: async function() {
      if (this.sessionInvalid) return; // Prevent multiple calls
      this.sessionInvalid = true;

      console.log('[FyncallWidget] Handling invalid session, clearing stored data...');

      // Use websocketManager to disconnect (prevents reconnect)
      _disconnectWebSocket(true);

      // Clear message cache for this session
      if (this.session) {
        clearMessageCache(this.session);
        try {
          await clearSessionMessages(this.session);
        } catch (e) { /* IndexedDB may not be available */ }
      }

      // Clear session storage
      clearSavedSession();

      // Reset state
      this.session = null;
      this.visitorToken = null;
      this.reconnectAttempts = 0;
      this._syncState();

      // Show message to user
      this.showStatus('Session expired. Creating new session...', 'info');

      // Wait a moment then reinitialize
      setTimeout(async () => {
        this.sessionInvalid = false;
        resetWebSocketSession();
        await this.createSession();
        this.showStatus('New session created!', 'success');
      }, 1000);
    },

    // Validate session with server
    validateSession: async function() {
      if (!this.session || !this.visitorToken) {
        this.handleInvalidSession();
        return;
      }

      try {
        const url = `${this.config.baseUrl}/api/v1/widget/sessions/${this.session}?visitor_token=${encodeURIComponent(this.visitorToken)}&include_messages=false`;
        const response = await fetch(url, {
          method: 'GET',
          headers: this.getHeaders()
        });

        if (!response.ok) {
          console.log('[FyncallWidget] Session validation failed:', response.status);
          this.handleInvalidSession();
        }
      } catch (error) {
        console.error('[FyncallWidget] Session validation error:', error);
        this.handleInvalidSession();
      }
    },

    handleWebSocketMessage: function(data) {
      if (data.type === 'typing' && data.data) {
        if (data.data.isTyping && data.data.sender?.type === 'agent' && this.config.showTypingIndicator !== false) {
          this.elements.typing.classList.add('show');
          this.scrollToBottom();
        } else {
          this.elements.typing.classList.remove('show');
        }
      } else if (data.type === 'message' && data.event === 'new_message') {
        const msg = data.data;
        if (msg && msg.sender_type !== 'visitor') {
          this.elements.typing.classList.remove('show');
          this.addMessage(msg.content, 'bot', msg.message_metadata);

          // Play notification sound if enabled

          const botText = msg.content || '';
          _showDesktopNotification({
            title: this.config.agentName || 'New Message',
            body: botText.length > 100 ? botText.substring(0, 100) + '...' : botText,
            icon: this.config.agentAvatarUrl || this.config.companyLogo
          });

          // Cache the received WebSocket message
          if (this.session) {
            appendMessageToCache(this.session, {
              id: msg.id || ('ws_' + Date.now()),
              content: msg.content,
              sender_type: msg.sender_type,
              message_metadata: msg.message_metadata,
              created_at: msg.created_at || new Date().toISOString()
            });
          }

          if (!this.isOpen) {
            this.unreadCount++;
            this.updateBadge();
          }
          // Handle cart actions from AI (web widget only)
          if (msg.message_metadata?.cart_action) {
            this.handleCartAction(msg.message_metadata.cart_action);
          }
        }
      }
    },

    handleFileSelect: function(e) {
      const file = e.target.files[0];
      if (!file) return;

      this.selectedFile = file;
      this.elements.fileName.textContent = file.name;
      this.elements.fileSize.textContent = formatFileSize(file.size);

      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => { this.elements.fileThumb.src = e.target.result; };
        reader.readAsDataURL(file);
      } else {
        this.elements.fileThumb.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%236c757d"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z"/></svg>';
      }
      this.elements.filePreview.classList.add('show');
    },

    clearFilePreview: function() {
      this.selectedFile = null;
      this.elements.fileInput.value = '';
      this.elements.filePreview.classList.remove('show');
    },

    sendMessage: async function(customContent) {
      const content = customContent || this.elements.input.value.trim();
      if (!content || !this.session) return;

      if (!customContent) {
        this.elements.input.value = '';
        // Reset textarea height after clearing
        this.elements.input.style.height = 'auto';
      }
      this.elements.sendBtn.disabled = true;
      this.addMessage(content, 'visitor');
      this.clearFilePreview();
      if (this.config.showTypingIndicator !== false) {
        this.elements.typing.classList.add('show');
      }
      this.scrollToBottom();

      // Mark that we now have a conversation (for next widget open)
      this.hasConversation = true;

      // Update activity timestamp on user message
      updateSessionActivity();

      // Cache the visitor message immediately (both localStorage and IndexedDB)
      const visitorMessage = {
        id: 'visitor_' + Date.now(),
        content: content,
        sender_type: 'visitor',
        created_at: new Date().toISOString()
      };
      appendMessageToCache(this.session, visitorMessage);
      appendMessageToIndexedDB(this.session, visitorMessage).catch(err =>
        console.warn('[FyncallWidget] Failed to append to IndexedDB:', err)
      );

      try {
        // Refresh cart data before sending message (ensures AI has latest cart state)
        await this.refreshCartData();

        // Build request body with message and cart context
        const requestBody = {
          content: content,
          message_type: 'text',
          sender_type: 'visitor'
        };

        // Include Shopify cart context if available (for cart-aware AI responses)
        console.log('[FyncallWidget] Cart state before sending:', {
          cartCount: this.cartCount,
          itemsCount: this.cartData?.items?.length || 0,
          total: this.cartData?.total
        });

        if (this.cartData && this.cartData.items && this.cartData.items.length > 0) {
          requestBody.cart_context = {
            item_count: this.cartCount || 0,
            total_price: this.cartData.total || 0,
            currency: this.cartData.currency || 'USD',
            items: this.cartData.items.map(item => ({
              product_id: item.product_id,
              variant_id: item.variant_id || item.id,  // Fallback to id if variant_id not present
              key: item.key,  // Line item key for reliable cart modifications
              title: item.title,
              variant_title: item.variant_title,
              quantity: item.quantity,
              price: item.price / 100,  // Convert from cents
              image: item.image
            }))
          };
          console.log('[FyncallWidget] Sending cart_context to AI:', requestBody.cart_context);
        } else {
          console.log('[FyncallWidget] No cart items found - cart_context will not be sent');
        }

        const url = `${this.config.baseUrl}/api/v1/widget/sessions/${this.session}/messages?visitor_token=${encodeURIComponent(this.visitorToken)}`;
        const response = await fetch(url, {
          method: 'POST',
          headers: this.getHeaders(),
          body: JSON.stringify(requestBody)
        });

        // Handle session not found / invalid session
        if (response.status === 404 || response.status === 401 || response.status === 403) {
          console.log('[FyncallWidget] Session error on send:', response.status);
          this.elements.typing.classList.remove('show');
          await this.handleInvalidSession();
          // Retry the message after session recreation
          setTimeout(() => this.sendMessage(content), 1500);
          return;
        }

        if (!response.ok) throw new Error(`Send failed: ${response.status}`);
        const data = await response.json();
        this.elements.typing.classList.remove('show');

        if (data.bot_response) {
          const botContent = data.bot_response.content;
          const messageMetadata = data.bot_response.message_metadata;
          const quickReplies = this.getContextualQuickReplies(botContent, messageMetadata);
          this.addMessage(botContent, 'bot', messageMetadata, quickReplies);

          // Cache the bot response (both localStorage and IndexedDB)
          const botMessage = {
            id: data.bot_response.id || ('bot_' + Date.now()),
            content: botContent,
            sender_type: 'bot',
            message_metadata: messageMetadata,
            created_at: new Date().toISOString()
          };
          appendMessageToCache(this.session, botMessage);
          appendMessageToIndexedDB(this.session, botMessage).catch(err =>
            console.warn('[FyncallWidget] Failed to append bot message to IndexedDB:', err)
          );

          // Handle cart actions from AI (web widget only)
          if (messageMetadata?.cart_action) {
            await this.handleCartAction(messageMetadata.cart_action);
          }
        }
      } catch (error) {
        console.error('[FyncallWidget] Send error:', error);
        this.elements.typing.classList.remove('show');

        // Check if error is session-related
        if (error.message && (error.message.includes('404') || error.message.includes('Session'))) {
          await this.handleInvalidSession();
          return;
        }

        this.addMessage('Sorry, something went wrong. Please try again.', 'bot');
      } finally {
        this.elements.sendBtn.disabled = false;
        this.elements.input.focus();
      }
    },

    getContextualQuickReplies: function(content, metadata) {
      const hasOrderStatus = metadata?.order_status;
      const contentLower = content?.toLowerCase() || '';

      // Contextual quick replies disabled
      return null;
    },


    // =====================================================================
    // MESSAGING
    // =====================================================================
    addMessage: function(content, type, messageMetadata, quickReplies) {
      // Check if we have product attachments that should be separated
      const hasProducts = messageMetadata && (messageMetadata.attachements?.length > 0 || messageMetadata.attachments?.length > 0);
      const hasTextContent = content && content.trim().length > 0;

      // If we have both text and products, render text first, then products separately
      if (hasTextContent && hasProducts) {
        // Create metadata without products for text message
        const textOnlyMetadata = { ...messageMetadata };
        delete textOnlyMetadata.attachements;
        delete textOnlyMetadata.attachments;

        // Render text message first (without products, without quick replies)
        this._renderSingleMessage(content, type, textOnlyMetadata, null);

        // Create metadata with only products for carousel message
        const productsOnlyMetadata = {
          attachements: messageMetadata.attachements || messageMetadata.attachments
        };

        // Render products carousel as separate message (no text, with quick replies)
        this._renderSingleMessage(null, type, productsOnlyMetadata, quickReplies, true);
      } else {
        // Normal case: render everything in one message
        this._renderSingleMessage(content, type, messageMetadata, quickReplies);
      }
    },

    // Internal method to render a single message bubble
    _renderSingleMessage: function(content, type, messageMetadata, quickReplies, isRichMessageOnly = false) {
      const msgDiv = document.createElement('div');
      msgDiv.className = `fyncall-message ${type}`;
      if (isRichMessageOnly) {
        msgDiv.classList.add('rich-message-only');
      }
      const msgId = 'msg_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5);
      msgDiv.id = msgId;

      // For bot messages: create header with avatar + name inline at top
      if (type === 'bot') {
        // Add avatar if enabled
        if (this.config.showAgentAvatar) {
          const avatarDiv = document.createElement('div');
          avatarDiv.className = 'fyncall-msg-avatar';
          if (this.config.agentAvatarUrl) {
            const img = document.createElement('img');
            img.src = this.config.agentAvatarUrl;
            img.alt = 'AI';
            img.onerror = () => { avatarDiv.innerHTML = '💬'; };
            avatarDiv.appendChild(img);
          } else {
            avatarDiv.innerHTML = '💬';
          }
          msgDiv.appendChild(avatarDiv);
        }

        // Add agent name
        const nameDiv = document.createElement('div');
        nameDiv.className = 'fyncall-agent-name';
        nameDiv.textContent = this.config.agentName || 'AI Assistant';
        msgDiv.appendChild(nameDiv);
      }

      // Create message wrapper (contains content, time, quick replies)
      const wrapperDiv = document.createElement('div');
      wrapperDiv.className = 'fyncall-message-wrapper';

      const contentDiv = document.createElement('div');
      contentDiv.className = 'fyncall-message-content';

      // Add text content
      if (content) {
        const textDiv = document.createElement('div');
        textDiv.innerHTML = this.formatMessage(content);
        contentDiv.appendChild(textDiv);
      }

      // Render rich message components from metadata
      this.renderMessageComponents(messageMetadata, contentDiv);

      wrapperDiv.appendChild(contentDiv);

      // Add timestamp and status
      const timeDiv = document.createElement('div');
      timeDiv.className = 'fyncall-message-time';
      timeDiv.innerHTML = _formatTime(new Date());
      // Add read receipt for visitor messages if enabled
      if (type === 'visitor' && this.config.showReadReceipts !== false) {
        timeDiv.innerHTML += ' <span class="fyncall-read-receipt read"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 7 17l-5-5"/><path d="m22 10-7.5 7.5L13 16"/></svg></span>';
      }
      wrapperDiv.appendChild(timeDiv);

      // Add quick replies after timestamp
      if (quickReplies && quickReplies.length > 0) {
        const repliesDiv = this.createQuickReplies(quickReplies);
        wrapperDiv.appendChild(repliesDiv);
      }

      msgDiv.appendChild(wrapperDiv);

      // Reactions disabled - removed per user request

      this.elements.messages.insertBefore(msgDiv, this.elements.typing);
      this.scrollToBottom();
    },

    formatMessage: function(text) {
      // Convert markdown-like formatting
      return text
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/\n/g, '<br>');
    },

    // Render rich message components based on metadata
    renderMessageComponents: function(metadata, container) {
      if (!metadata) return;

      // Component renderers map
      const renderers = [
        // Product carousel (supports both spellings)
        { key: ['attachements', 'attachments'], fn: (data) => data?.length > 0 ? this.createProductCarousel(data) : null },
        { key: 'order_status', fn: (data) => this.createOrderStatusCard(data) },
        { key: 'comparison', fn: (data) => this.createComparisonCard(data) },
        { key: 'info_card', fn: (data) => this.createInfoCard(data) },
        { key: 'features', fn: (data) => this.createFeatureList(data) },
        { key: 'category_browser', fn: (data) => this.createCategoryBrowser(data) },
        { key: 'ticket_status', fn: (data) => this.createTicketStatusCard(data) },
        { key: 'product_detail', fn: (data) => this.createProductDetailCard(data) },
        { key: 'order_detail', fn: (data) => this.createOrderDetailCard(data) },
        { key: 'collection_browser', fn: (data) => this.createCollectionBrowser(data) },
        { key: 'quick_actions', fn: (data) => this.createQuickActionCards(data) },
        { key: 'cart_panel', fn: (data) => this.createCartPanelCard(data) }
      ];

      renderers.forEach(({ key, fn }) => {
        // Handle array of keys (for alternate spellings)
        const keys = Array.isArray(key) ? key : [key];
        for (const k of keys) {
          if (metadata[k]) {
            const component = fn(metadata[k]);
            if (component) container.appendChild(component);
            break;
          }
        }
      });
    },


    // =====================================================================
    // PRODUCT COMPONENTS (delegated to modules)
    // =====================================================================
    createProductCarousel: function(products) {
      return _createProductCarousel(products, {
        onProductClick: (product) => this.handleProductClick(product),
        onAddToCart: (product, btn) => this.handleAddToCart(product, btn),
        onOpenVariantModal: (product) => this.openVariantModal(product),
        isInCart: (product) => this.isProductInCart(product)
      });
    },

    // Check if a product is already in cart
    isProductInCart: function(product) {
      const productId = product.id || product.product_id;
      const variantId = product.shopify_variant_id || product.variant_id;

      // Check if added during this session
      if (this.addedToCartIds.has(productId) || this.addedToCartIds.has(variantId)) {
        return true;
      }

      // Check existing cart data
      if (this.cartData?.items?.length > 0) {
        return this.cartData.items.some(item =>
          item.product_id === productId ||
          item.variant_id === variantId ||
          item.id === productId
        );
      }

      // Check Shopify cart if available
      if (this.shopifyCart?.items?.length > 0) {
        return this.shopifyCart.items.some(item =>
          item.product_id === productId ||
          item.variant_id === variantId ||
          item.id === variantId
        );
      }

      return false;
    },

    createProductCard: function(product) {
      return _createProductCard(product, {
        onProductClick: (product) => this.handleProductClick(product),
        onAddToCart: (product, btn) => this.handleAddToCart(product, btn),
        onOpenVariantModal: (product) => this.openVariantModal(product)
      });
    },

    createOrderStatusCard: function(order) {
      return _createOrderStatusCard(order);
    },

    createComparisonCard: function(comparison) {
      const card = document.createElement('div');
      card.className = 'fyncall-comparison-card';

      // Header
      if (comparison.title) {
        const header = document.createElement('div');
        header.className = 'fyncall-comparison-header';
        header.textContent = comparison.title;
        card.appendChild(header);
      }

      // Table
      const table = document.createElement('table');
      table.className = 'fyncall-comparison-table';

      const products = comparison.products || [];
      const attributes = comparison.attributes || ['Price', 'Rating', 'In Stock'];

      // Header row with product names
      const headerRow = document.createElement('tr');
      const emptyTh = document.createElement('th');
      headerRow.appendChild(emptyTh);

      products.forEach(product => {
        const th = document.createElement('th');
        th.textContent = product.name || 'Product';
        th.style.fontWeight = '600';
        th.style.color = 'var(--fyncall-text)';
        if (product.isRecommended) {
          th.classList.add('fyncall-comparison-winner');
        }
        headerRow.appendChild(th);
      });
      table.appendChild(headerRow);

      // Attribute rows
      attributes.forEach(attr => {
        const row = document.createElement('tr');
        const labelCell = document.createElement('th');
        labelCell.textContent = attr;
        row.appendChild(labelCell);

        products.forEach(product => {
          const td = document.createElement('td');
          const value = product[attr.toLowerCase().replace(/ /g, '_')];
          td.textContent = value !== undefined ? String(value) : '-';

          if (product.isRecommended) {
            td.classList.add('fyncall-comparison-winner');
          }
          row.appendChild(td);
        });
        table.appendChild(row);
      });

      card.appendChild(table);
      return card;
    },

    // Info card - delegates to rich-messages.js module
    createInfoCard: function(info) {
      return _createInfoCard(info, { onSendMessage: (msg) => this.sendMessage(msg) });
    },

    // Feature list - delegates to rich-messages.js module
    createFeatureList: function(features) {
      return _createFeatureList(features);
    },

    // Category browser - delegates to rich-messages.js module
    createCategoryBrowser: function(data) {
      return _createCategoryBrowser(data, { onSendMessage: (msg) => this.sendMessage(msg) });
    },

    // Category icon - delegates to rich-messages.js module
    getCategoryIcon: function(categoryName) {
      return _getCategoryIcon(categoryName);
    },


    // =====================================================================
    // TICKET COMPONENTS
    // =====================================================================
    createTicketStatusCard: function(ticket) {
      const card = document.createElement('div');
      card.className = 'fyncall-ticket-card';

      // Header
      const header = document.createElement('div');
      header.className = 'fyncall-ticket-header';
      header.innerHTML = `
                <div class="fyncall-ticket-header-top">
                    <span class="fyncall-ticket-id">🎫 Ticket #${ticket.ticket_number || ticket.ticket_id}</span>
                    ${ticket.priority ? `<span class="fyncall-ticket-priority ${ticket.priority}">${ticket.priority}</span>` : ''}
                </div>
                ${ticket.subject ? `<div class="fyncall-ticket-subject">"${ticket.subject}"</div>` : ''}
            `;
      card.appendChild(header);

      // Body
      const body = document.createElement('div');
      body.className = 'fyncall-ticket-body';

      // Status badge
      const status = (ticket.status || 'open').toLowerCase().replace(/ /g, '_');
      const statusLabels = {
        'open': 'Open',
        'in_progress': 'In Progress',
        'pending_review': 'Pending Review',
        'resolved': 'Resolved',
        'closed': 'Closed'
      };
      body.innerHTML = `
                <div class="fyncall-ticket-status-badge">
                    <span class="fyncall-ticket-status-dot ${status}"></span>
                    Status: ${statusLabels[status] || status}
                </div>
            `;

      // Progress tracker
      const progress = document.createElement('div');
      progress.className = 'fyncall-ticket-progress';
      const steps = [
        { key: 'open', label: 'Open' },
        { key: 'in_progress', label: 'Working' },
        { key: 'pending_review', label: 'Review' },
        { key: 'resolved', label: 'Resolved' }
      ];
      const statusOrder = ['open', 'in_progress', 'pending_review', 'resolved', 'closed'];
      const currentIndex = statusOrder.indexOf(status);

      steps.forEach((step, index) => {
        const stepDiv = document.createElement('div');
        stepDiv.className = 'fyncall-ticket-step';
        if (index < currentIndex) stepDiv.classList.add('completed');
        else if (index === currentIndex) stepDiv.classList.add('active');
        stepDiv.innerHTML = `
                    <div class="fyncall-ticket-step-icon">${index <= currentIndex ? '✓' : ''}</div>
                    <span class="fyncall-ticket-step-label">${step.label}</span>
                `;
        progress.appendChild(stepDiv);
      });
      body.appendChild(progress);

      // Details
      const details = document.createElement('div');
      details.className = 'fyncall-ticket-details';
      if (ticket.created_at) {
        details.innerHTML += `<div class="fyncall-ticket-detail">📅 Created: ${_formatDate(ticket.created_at)}</div>`;
      }
      if (ticket.assigned_to) {
        details.innerHTML += `<div class="fyncall-ticket-detail">👤 Assigned: ${ticket.assigned_to}</div>`;
      }
      if (ticket.attachment_count) {
        details.innerHTML += `<div class="fyncall-ticket-detail">📎 Attachments: ${ticket.attachment_count} files</div>`;
      }
      if (ticket.updated_at) {
        details.innerHTML += `<div class="fyncall-ticket-detail">💬 Updated: ${formatRelativeTime(ticket.updated_at)}</div>`;
      }
      body.appendChild(details);
      card.appendChild(body);

      // Actions
      const actions = document.createElement('div');
      actions.className = 'fyncall-ticket-actions';
      actions.innerHTML = `
                <button class="fyncall-ticket-btn secondary" onclick="FyncallWidgetSDK.sendMessage('Add a comment to ticket ${ticket.ticket_number || ticket.ticket_id}')">💬 Add Comment</button>
                <button class="fyncall-ticket-btn primary" onclick="FyncallWidgetSDK.sendMessage('Show details of ticket ${ticket.ticket_number || ticket.ticket_id}')">View Details</button>
            `;
      card.appendChild(actions);

      return card;
    },


    // =====================================================================
    // PRODUCT DETAIL MODAL (delegated to module)
    // =====================================================================
    createProductDetailCard: function(data) {
      return _createProductDetailCard(data, {
        onAddToCart: (product, qty) => {
          this.cartCount += qty;
          this.updateCartBadge();
          this.showStatus(`Added ${qty}x "${product.name}" to cart!`, 'success');
        },
        onWishlist: (product) => {
          this.showStatus('Added to wishlist!', 'success');
        },
        onSendMessage: (msg) => this.sendMessage(msg),
        onError: (error) => {
          console.error('[FyncallWidget] Add to cart error:', error);
          this.showStatus('Failed to add item. Please try again.', 'error');
        },
        onSelectOptions: () => {
          this.showStatus('Please select all options', 'error');
        }
      });
    },

    // Keep updateDetailQty and addDetailToCart as thin wrappers for global access
    updateDetailQty: function(delta) {
      _updateDetailQty(delta);
    },

    addDetailToCart: async function() {
      await _addDetailToCart({
        onSuccess: (product, qty) => {
          this.cartCount += qty;
          this.updateCartBadge();
          this.showStatus(`Added ${qty}x "${product.name}" to cart!`, 'success');
        },
        onError: (error) => {
          console.error('[FyncallWidget] Add to cart error:', error);
          this.showStatus('Failed to add item. Please try again.', 'error');
        },
        onSelectOptions: () => {
          this.showStatus('Please select all options', 'error');
        }
      });
    },


    // =====================================================================
    // ORDER DETAIL (delegated to module)
    // =====================================================================
    createOrderDetailCard: function(order) {
      return _createOrderDetailCard(order, {
        onSendMessage: (msg) => this.sendMessage(msg),
        onReorder: (order) => this.handleReorderItems(order),
        onOpenInvoice: (order) => this.openOrderInvoice(order)
      });
    },

    // Handle reorder - add all items back to cart via Storefront API
    handleReorderItems: async function(order) {
      if (!order.items || order.items.length === 0) {
        this.showToast('No items to reorder', 'error');
        return;
      }

      const orderNum = order.order_number || order.order_id;
      this.showToast('Adding items to cart...', 'info');

      try {
        // Build items array for Shopify cart
        const items = order.items
          .filter(item => item.variant_id || item.product_id)
          .map(item => ({
            id: item.variant_id || item.product_id,
            quantity: item.quantity || 1
          }));

        if (items.length === 0) {
          // Fallback: send message to AI if no variant IDs
          this.sendMessage(`I want to reorder items from order ${orderNum}`);
          return;
        }

        // Call Shopify cart API
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items })
        });

        if (response.ok) {
          const result = await response.json();
          this.showToast(`${result.items?.length || items.length} item(s) added to cart!`, 'success');

          // Refresh cart state
          const cartResponse = await fetch('/cart.js');
          if (cartResponse.ok) {
            this.shopifyCart = await cartResponse.json();
          }
        } else {
          throw new Error('Failed to add items');
        }
      } catch (error) {
        console.error('Reorder error:', error);
        // Fallback to AI
        this.sendMessage(`I want to reorder items from order ${orderNum}`);
      }
    },

    // Open order invoice/status page
    openOrderInvoice: function(order) {
      // Try to open Shopify order status page if we have the info
      if (order.order_status_url) {
        window.open(order.order_status_url, '_blank');
      } else if (order.shopify_order_id) {
        // Construct Shopify order status URL (customer needs to be logged in)
        const shopDomain = window.location.hostname;
        window.open(`https://${shopDomain}/account/orders/${order.shopify_order_id}`, '_blank');
      } else {
        // Fallback: send message to AI
        this.sendMessage(`I need the invoice for order ${order.order_number || order.order_id}`);
      }
    },

    createQuickActionCards: function(data) {
      return _createQuickActionCards(data, (msg) => this.sendMessage(msg));
    },

    createCollectionBrowser: function(data) {
      return _createCollectionBrowser(data, (msg) => this.sendMessage(msg));
    },

    /**
     * Create an interactive cart panel card for display in chat
     */

    // =====================================================================
    // CART PANEL (delegated to module)
    // =====================================================================
    createCartPanelCard: function(data) {
      return _createCartPanelCard(data, {
        onBrowse: () => this.sendMessage('Show me popular products'),
        onQuantityChange: (item, delta, itemEl) => this.handleCartPanelQuantityChange(item, delta, itemEl),
        onRemove: (item, itemEl) => this.handleCartPanelRemove(item, itemEl),
        onApplyPromo: (code, section) => this.handleCartPanelPromoCode(code, section),
        onCheckout: () => this.handleCheckout(),
        onContinueShopping: () => this.sendMessage('Show me more products')
      });
    },

    /**
     * Handle quantity change for cart panel items - directly calls Shopify Cart API
     */
    handleCartPanelQuantityChange: async function(item, delta, itemEl) {
      const newQuantity = item.quantity + delta;

      if (newQuantity < 1) {
        // Remove item if quantity would be 0
        this.handleCartPanelRemove(item, itemEl);
        return;
      }

      // Update UI immediately for responsiveness
      const qtyValue = itemEl.querySelector('.fyncall-cart-panel-qty-value');
      const originalQty = qtyValue.textContent;
      qtyValue.textContent = newQuantity;

      // Disable buttons during update
      const minusBtn = itemEl.querySelector('.minus');
      const plusBtn = itemEl.querySelector('.plus');
      minusBtn.disabled = true;
      plusBtn.disabled = true;

      try {
        // Call Shopify Cart API directly
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: item.key || item.variant_id,
            quantity: newQuantity
          })
        });

        if (!response.ok) {
          throw new Error('Failed to update cart');
        }

        const cart = await response.json();

        // Update item's local quantity and line total
        item.quantity = newQuantity;
        _updateCartItemTotal(itemEl, newQuantity, item.price);

        // Update cart panel totals
        this.updateCartPanelTotals(itemEl.closest('.fyncall-cart-panel-card'), cart);

        // Also refresh the main cart data and badge
        this.cartData = {
          items: cart.items || [],
          total: cart.total_price / 100 || 0,
          currency: cart.currency || 'USD'
        };
        this.cartCount = cart.item_count || 0;
        this.updateCartBadge();

        console.log(`[FyncallWidget] Cart updated: ${item.name} qty=${newQuantity}`);

      } catch (error) {
        console.error('[FyncallWidget] Failed to update cart:', error);
        // Revert UI on error
        qtyValue.textContent = originalQty;
        this.showToast('Failed to update cart. Please try again.', 'error');
      } finally {
        // Re-enable buttons
        minusBtn.disabled = false;
        plusBtn.disabled = false;
      }
    },

    /**
     * Handle item removal from cart panel - directly calls Shopify Cart API
     */
    handleCartPanelRemove: async function(item, itemEl) {
      // Add removing animation
      itemEl.classList.add('removing');

      try {
        // Call Shopify Cart API to remove item (set quantity to 0)
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: item.key || item.variant_id,
            quantity: 0
          })
        });

        if (!response.ok) {
          throw new Error('Failed to remove item');
        }

        const cart = await response.json();

        // Remove item from DOM after animation
        setTimeout(() => {
          itemEl.remove();
          const cartCard = itemEl.closest('.fyncall-cart-panel-card');
          const itemsContainer = cartCard?.querySelector('.fyncall-cart-panel-items');
          if (itemsContainer && itemsContainer.children.length === 0) {
            _showEmptyCartState(cartCard, () => this.sendMessage('Show me popular products'));
          } else {
            this.updateCartPanelTotals(cartCard, cart);
          }
        }, 300);

        // Update main cart data and badge
        this.cartData = {
          items: cart.items || [],
          total: cart.total_price / 100 || 0,
          currency: cart.currency || 'USD'
        };
        this.cartCount = cart.item_count || 0;
        this.updateCartBadge();

        console.log(`[FyncallWidget] Item removed: ${item.name}`);

      } catch (error) {
        console.error('[FyncallWidget] Failed to remove item:', error);
        // Revert animation on error
        itemEl.classList.remove('removing');
        this.showToast('Failed to remove item. Please try again.', 'error');
      }
    },

    updateCartPanelTotals: function(cartCard, cart) {
      _updateCartPanelTotals(cartCard, cart, 75);
    },

    // Toast notification - delegates to toast.js module
    showToast: function(message, type = 'info') {
      _showToast(message, type, { container: this.shadowRoot });
    },

    /**
     * Handle promo code application from cart panel
     */
    handleCartPanelPromoCode: async function(code, promoSection) {
      if (!code || !code.trim()) {
        return;
      }

      const applyBtn = promoSection.querySelector('.fyncall-cart-panel-promo-apply');
      const promoInput = promoSection.querySelector('.fyncall-cart-panel-promo-input');
      const originalText = applyBtn.textContent;
      applyBtn.textContent = 'Applying...';
      applyBtn.disabled = true;

      try {
        // Note: Shopify doesn't have a direct API for promo codes in the cart
        // Promo codes are typically applied at checkout
        // We'll show a message directing users to checkout
        this.showToast('Promo codes are applied at checkout. Proceeding...', 'info');

        // Optionally redirect to checkout with discount code
        const checkoutUrl = `/checkout?discount=${encodeURIComponent(code.trim())}`;

        // Show success feedback
        promoInput.value = '';
        applyBtn.textContent = 'Applied!';

        setTimeout(() => {
          applyBtn.textContent = originalText;
          applyBtn.disabled = false;
        }, 1500);

      } catch (error) {
        console.error('[FyncallWidget] Failed to apply promo:', error);
        applyBtn.textContent = originalText;
        applyBtn.disabled = false;
        this.showToast('Failed to apply promo code', 'error');
      }
    },

    // ========== END NEW INTERACTIVE MESSAGE TYPES ==========

    createQuickReplies: function(replies) {
      const container = document.createElement('div');
      container.className = 'fyncall-quick-replies';

      replies.forEach(reply => {
        const btn = document.createElement('button');
        btn.className = 'fyncall-quick-reply';
        btn.textContent = reply.label;
        btn.onclick = () => {
          // Handle special actions
          if (reply.action === '__view_cart__') {
            this.openCartPanel();
          } else if (reply.action === '__checkout__') {
            this.handleCheckout();
          } else {
            this.sendMessage(reply.action);
          }
          container.remove();
        };
        container.appendChild(btn);
      });

      return container;
    },

    createReactionButtons: function(msgId, msgElement) {
      // Create reaction trigger (appears on hover)
      const trigger = document.createElement('div');
      trigger.className = 'fyncall-reaction-trigger';
      trigger.innerHTML = '😊';
      trigger.title = 'React to message';

      // Create reaction picker (popup)
      const picker = document.createElement('div');
      picker.className = 'fyncall-reaction-picker';
      picker.id = `reaction-picker-${msgId}`;

      // Use configured emojis or fall back to defaults
      const reactions = this.messageStylingConfig?.reaction_emojis || ['👍', '👎', '❤️', '😂', '😮', '😢'];
      const displayReactions = reactions.slice(0, 6);

      displayReactions.forEach(emoji => {
        const btn = document.createElement('button');
        btn.className = 'fyncall-reaction-btn';
        btn.textContent = emoji;
        btn.onclick = (e) => {
          e.stopPropagation();
          this.handleReaction(msgId, emoji, msgElement);
          picker.classList.remove('show');
        };
        picker.appendChild(btn);
      });

      // Toggle picker on trigger click
      trigger.onclick = (e) => {
        e.stopPropagation();
        // Close any other open pickers
        this.elements.messages.querySelectorAll('.fyncall-reaction-picker.show').forEach(p => {
          if (p !== picker) p.classList.remove('show');
        });
        picker.classList.toggle('show');
      };

      // Close picker when clicking outside
      document.addEventListener('click', () => {
        picker.classList.remove('show');
      }, { once: false });

      return { trigger, picker };
    },

    handleReaction: function(msgId, emoji, msgElement) {
      // Remove existing reaction badge if any
      const existingReaction = msgElement.querySelector('.fyncall-message-reaction');
      if (existingReaction) {
        // If clicking same emoji, remove reaction
        if (existingReaction.textContent === emoji) {
          existingReaction.remove();
          delete this.messageReactions[msgId];
          console.log('[FyncallWidget] Reaction removed:', msgId);
          return;
        }
        existingReaction.remove();
      }

      // Add new reaction badge
      const badge = document.createElement('div');
      badge.className = 'fyncall-message-reaction';
      badge.textContent = emoji;
      badge.title = 'Click to remove';
      badge.onclick = (e) => {
        e.stopPropagation();
        badge.remove();
        delete this.messageReactions[msgId];
        console.log('[FyncallWidget] Reaction removed:', msgId);
      };
      msgElement.appendChild(badge);

      this.messageReactions[msgId] = emoji;
      console.log('[FyncallWidget] Reaction added:', msgId, emoji);
    },


    // =====================================================================
    // SHOPIFY INTEGRATION
    // =====================================================================
    handleProductClick: function(product) {
      if (product.public_url) {
        window.open(product.public_url, '_blank');
      }
    },

    handleAddToCart: async function(product, btn) {
      const shopifyVariantId = product.shopify_variant_id || product.variant_id;
      const productId = product.id || product.product_id;

      if (!shopifyVariantId) {
        if (product.public_url) window.open(product.public_url, '_blank');
        return;
      }

      btn.disabled = true;
      const originalText = btn.textContent;
      btn.textContent = 'Adding...';

      try {
        // Use imported Shopify module for add to cart
        const result = await _shopifyAddToCart(shopifyVariantId, 1);

        if (result) {
          // Track this product as added to cart
          if (productId) this.addedToCartIds.add(productId);
          if (shopifyVariantId) this.addedToCartIds.add(shopifyVariantId);

          btn.classList.add('added');
          btn.textContent = '✓ Added';
          // Keep button disabled to prevent duplicate adds
          this.cartCount++;
          this.updateCartBadge();
          this.showStatus(`Added "${product.name}" to cart!`, 'success');
          // Don't re-enable button - keep it in "Added" state
          return;
        } else {
          throw new Error('Failed');
        }
      } catch (error) {
        console.error('[FyncallWidget] Add to cart error:', error);
        btn.textContent = originalText;
        btn.disabled = false;
        if (product.public_url) window.open(product.public_url, '_blank');
      }
    },

    updateCartBadge: async function() {
      // Use imported Shopify module to get cart
      const cart = await getShopifyCart();
      if (cart) {
        this.cartCount = cart.item_count || 0;
        this.cartData = {
          items: cart.items || [],
          total: cart.total_price / 100 || 0
        };
        // Update header cart badge
        if (this.elements.headerCartBadge) {
          this.elements.headerCartBadge.textContent = this.cartCount;
          this.elements.headerCartBadge.classList.toggle('show', this.cartCount > 0);
        }
        // Update cart panel count
        if (this.elements.cartPanelCount) {
          this.elements.cartPanelCount.textContent = this.cartCount;
        }
      }
    },

    // =====================================================
    // SHOPIFY CONTEXT DETECTION
    // =====================================================
    // SHOPIFY CONTEXT DETECTION (delegated to module)
    // =====================================================

    /**
     * Detect Shopify customer and cart context - delegates to module
     */
    detectShopifyCustomer: function() {
      this.shopifyContext = _detectShopifyCustomerFull();
      return this.shopifyContext;
    },

    /**
     * Get saved visitor info from localStorage
     */
    getSavedVisitorInfo: function() {
      return getVisitorInfoFromStorage();
    },

    /**
     * Save visitor info to localStorage
     */
    saveVisitorInfo: function(info) {
      saveVisitorInfoToStorage(info);
    },

    /**
     * Auto-identify customer - delegates to module
     */
    autoIdentifyCustomer: function(shopifyCustomer) {
      return _autoIdentifyCustomer(shopifyCustomer);
    },

    /**
     * Get best available visitor info - delegates to module
     */
    getVisitorInfo: function() {
      if (!this.shopifyContext) {
        this.detectShopifyCustomer();
      }
      return _getShopifyVisitorInfo({
        shopifyContext: this.shopifyContext,
        prechatFormData: this.prechatFormData
      });
    },

    /**
     * Extract custom fields - delegates to module
     */
    extractCustomFields: function(formData) {
      return _extractCustomFields(formData);
    },

    /**
     * Get Shopify context for session - delegates to module
     */
    getShopifyContextForSession: function() {
      if (!this.shopifyContext) {
        this.detectShopifyCustomer();
      }
      return _getShopifyContextForSessionFull(this.shopifyContext);
    },

    // =====================================================
    // PRE-CHAT FORM METHODS
    // =====================================================

    /**
     * Determine if we should show the pre-chat form
     * Returns true for anonymous visitors when pre-chat is enabled
     */

    // =====================================================================
    // PRE-CHAT FORM (delegated to module)
    // =====================================================================
    shouldShowPrechatForm: function() {
      // First, detect Shopify context if not already done
      if (!this.shopifyContext) {
        this.detectShopifyCustomer();
      }
      return _shouldShowPrechatForm(this.config, this.tenantConfig, {
        shopifyContext: this.shopifyContext,
        savedVisitor: this.getSavedVisitorInfo(),
        savedSession: this.getSavedSession()
      });
    },

    showPrechatForm: function() {
      const formsConfig = this.tenantConfig?.forms;
      showPrechatFormUI({
        prechatForm: this.elements.prechatForm,
        messages: this.elements.messages,
        inputArea: this.elements.container.querySelector('#fyncall-input-area'),
        prechatContinue: this.elements.prechatContinue
      }, formsConfig);

      // Update prechat form avatar with company branding
      const prechatAvatar = this.elements.prechatForm?.querySelector('.fyncall-prechat-avatar');
      if (prechatAvatar) {
        const companyLogo = this.config.companyLogo;
        const companyName = this.config.companyName || '';
        const companyInitials = (companyName || 'FY').substring(0, 2).toUpperCase();

        if (companyLogo) {
          prechatAvatar.innerHTML = `<img src="${companyLogo}" alt="" style="width:100%;height:100%;object-fit:cover;border-radius:50%;" onerror="this.parentElement.textContent='${companyInitials}'">`;
        } else if (companyName) {
          prechatAvatar.textContent = companyInitials;
        }
        // Keep the wave emoji if no company branding is set
      }
    },

    renderDynamicPrechatFields: function(fields, formsConfig) {
      const formContainer = this.elements.prechatForm.querySelector('.fyncall-prechat-form');
      _renderDynamicPrechatFields(formContainer, fields, formsConfig);
    },

    hidePrechatForm: function() {
      hidePrechatFormUI({
        prechatForm: this.elements.prechatForm,
        messages: this.elements.messages,
        inputArea: this.elements.container.querySelector('#fyncall-input-area')
      });
      // Update header to ensure correct company name/avatar is displayed
      this.updateHeader();
    },

    handlePrechatContinue: function() {
      const formContainer = this.elements.prechatForm.querySelector('.fyncall-prechat-form');
      const { formData, hasErrors } = validatePrechatForm(formContainer, {
        prechatName: this.elements.prechatName,
        prechatEmail: this.elements.prechatEmail
      });

      if (hasErrors) {
        console.log('[FyncallWidget] Pre-chat form validation failed');
        return;
      }

      // Save visitor info
      if (formData.name || formData.email) {
        this.saveVisitorInfo({ name: formData.name || '', email: formData.email || '' });
      }

      this.prechatFormData = formData;
      console.log('[FyncallWidget] Pre-chat form data collected:', formData);
      this.hidePrechatForm();
      this.createSession();
    },

    handlePrechatSkip: function() {
      console.log('[FyncallWidget] Visitor skipped pre-chat form');
      this.hidePrechatForm();
      this.createSession();
    },

    // =====================================================================
    // CSAT SURVEY (delegated to module)
    // =====================================================================
    showCsatSurvey: function() {
      const formsConfig = this.tenantConfig?.forms;
      const csatElements = {
        csatStars: this.elements.csatStars,
        csatThumbs: this.elements.csatThumbs,
        csatNps: this.elements.csatNps,
        csatNpsLabels: this.elements.csatNpsLabels,
        csatQuestion: this.elements.csatQuestion,
        csatFollowup: this.elements.csatFollowup,
        csatFollowupLabel: this.elements.csatFollowupLabel,
        csatSkip: this.elements.csatSkip,
        csatThankyouText: this.elements.csatThankyouText,
        csatContent: this.elements.csatContent,
        csatThankyou: this.elements.csatThankyou,
        csatActions: this.elements.csatActions,
        csatPanel: this.elements.csatPanel,
        csatSubmit: this.elements.csatSubmit
      };
      const state = showCsatSurveyUI(csatElements, formsConfig);
      if (state) {
        this.csatState = state;
      }
    },

    configureCsatType: function(formsConfig) {
      const csatElements = {
        csatStars: this.elements.csatStars,
        csatThumbs: this.elements.csatThumbs,
        csatNps: this.elements.csatNps,
        csatNpsLabels: this.elements.csatNpsLabels
      };
      configureCsatType(csatElements, formsConfig.postchat_type || 'stars');
    },

    handleStarRating: function(rating) {
      _handleStarRating({
        csatStars: this.elements.csatStars,
        csatSubmit: this.elements.csatSubmit
      }, this.csatState, rating);
    },

    handleThumbsRating: function(rating) {
      _handleThumbsRating({
        csatThumbs: this.elements.csatThumbs,
        csatSubmit: this.elements.csatSubmit
      }, this.csatState, rating);
    },

    handleNpsRating: function(rating) {
      _handleNpsRating({
        csatNps: this.elements.csatNps,
        csatSubmit: this.elements.csatSubmit
      }, this.csatState, rating);
    },

    submitCsatSurvey: async function() {
      if (this.csatState.rating === null) return;
      const feedback = this.elements.csatFeedback.value.trim();
      await submitCsatToBackend(this.config, this.session, this.visitorToken, this.csatState, feedback, () => this.getHeaders());
      this.showCsatThankYou();
    },

    showCsatThankYou: function() {
      _showCsatThankYou({
        csatContent: this.elements.csatContent,
        csatActions: this.elements.csatActions,
        csatThankyou: this.elements.csatThankyou
      }, this.csatState, () => this.closeCsatSurvey());
    },

    closeCsatSurvey: function() {
      _closeCsatSurvey({ csatPanel: this.elements.csatPanel });
      if (this.pendingEndConversation) {
        console.log('[FyncallWidget] Completing end conversation after CSAT survey');
        this.completeEndConversation();
      }
    },

    onConversationEnd: function() {
      const formsConfig = this.tenantConfig?.forms;
      if (formsConfig?.postchat_enabled && !this.csatState?.submitted) {
        setTimeout(() => this.showCsatSurvey(), 1500);
      }
    },

    /**
     * Confirm before starting a new session
     * Shows a confirmation to prevent accidental clicks
     */
    confirmNewSession: function() {
      // Simple confirmation - could be replaced with a modal
      const confirmed = confirm(
        'Start a new conversation?\n\n' +
        '• Your current chat history will be cleared\n' +
        '• Your cart items are safe and won\'t be affected\n' +
        '• You can always view past conversations in your account'
      );

      if (confirmed) {
        this.startNewSession();
      }
    },

    /**
     * Start a completely new session
     * Clears current session but preserves Shopify context (cart, customer)
     */
    startNewSession: function() {
      console.log('[FyncallWidget] Starting new session...');

      // Close WebSocket connection using module
      _disconnectWebSocket(true);
      resetWebSocketSession();

      // Clear current session state
      this.session = null;
      this.visitorToken = null;
      this.clearSavedSession();

      // Clear messages from UI
      this.elements.messages.innerHTML = `
                <div class="fyncall-typing">
                    <div class="fyncall-typing-dots"><span></span><span></span><span></span></div>
                    <span class="fyncall-typing-text">Luna is typing...</span>
                </div>
            `;

      // Re-query the typing element after replacing innerHTML (old reference is stale)
      this.elements.typing = this.elements.messages.querySelector('.fyncall-typing');

      // Re-detect Shopify context (fresh cart data, customer status)
      this.shopifyContext = null;
      this.detectShopifyCustomer();
      this.updateCartBadge();

      // Reset reconnect attempts
      this.reconnectAttempts = 0;
      this.sessionInvalid = false;

      // Show status
      this.showStatus('Starting new conversation...', 'info');

      // Decide: show pre-chat form or create session directly
      // For new session, we skip the pre-chat form since user already chatted before
      // (they're not truly anonymous anymore)
      this.createSession();

      console.log('[FyncallWidget] New session initiated');
    },

    // =====================================================
    // CART PANEL METHODS
    // =====================================================

    openCartPanel: async function() {
      await this.loadCart();
      this.elements.cartPanel.classList.add('open');
    },

    closeCartPanel: function() {
      this.elements.cartPanel.classList.remove('open');
    },

    loadCart: async function() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        this.cartData = {
          items: cart.items || [],
          total: cart.total_price / 100 || 0,
          currency: cart.currency || 'USD'
        };
        this.cartCount = cart.item_count || 0;
        this.renderCartItems();
      } catch (error) {
        console.error('[FyncallWidget] Failed to load cart:', error);
      }
    },

    /**
     * Refresh cart data silently (without rendering) - used before sending messages
     */
    refreshCartData: async function() {
      try {
        const response = await fetch('/cart.js');
        if (!response.ok) return;

        const cart = await response.json();
        this.cartData = {
          items: cart.items || [],
          total: cart.total_price / 100 || 0,
          currency: cart.currency || 'USD'
        };
        this.cartCount = cart.item_count || 0;
        this.shopifyCart = cart; // Store full cart for isProductInCart checks
        this.updateCartBadge();
        console.log(`[FyncallWidget] Cart refreshed: ${this.cartCount} items, $${this.cartData.total}`);

        // Refresh any cart panels visible in the chat
        this.refreshCartPanelsInMessages(cart);
      } catch (error) {
        // Silently fail - might not be on Shopify store
        console.log('[FyncallWidget] Cart refresh skipped (not on Shopify store)');
      }
    },

    /**
     * Refresh all cart panels in chat messages with fresh Shopify cart data
     */
    refreshCartPanelsInMessages: function(cart) {
      if (!this.elements.messages) return;

      // Find all cart panel cards in messages
      const cartPanels = this.elements.messages.querySelectorAll('.fyncall-cart-panel-card');
      if (cartPanels.length === 0) return;

      console.log(`[FyncallWidget] Refreshing ${cartPanels.length} cart panels with ${cart.items?.length || 0} cart items`);

      cartPanels.forEach(panel => {
        // Check if cart is empty
        if (!cart.items || cart.items.length === 0) {
          _showEmptyCartState(panel, () => this.sendMessage('Show me popular products'));
          return;
        }

        // Update each item in the panel
        cart.items.forEach(shopifyItem => {
          // Try to find item by variant_id (handle both string and number)
          const variantId = String(shopifyItem.variant_id);
          let itemEl = panel.querySelector(`.fyncall-cart-panel-item[data-variant-id="${variantId}"]`);

          // Also try without class in case of attribute mismatch
          if (!itemEl) {
            itemEl = panel.querySelector(`[data-variant-id="${variantId}"]`);
          }

          if (itemEl) {
            console.log(`[FyncallWidget] Updating cart item ${variantId}: qty=${shopifyItem.quantity}`);
            // Update quantity
            const qtyValue = itemEl.querySelector('.fyncall-cart-panel-qty-value');
            if (qtyValue) {
              qtyValue.textContent = shopifyItem.quantity;
            }
            // Update line total (Shopify returns line_price in cents)
            const lineTotal = itemEl.querySelector('.fyncall-cart-panel-item-total');
            if (lineTotal) {
              const price = shopifyItem.line_price / 100;
              lineTotal.textContent = '$' + price.toFixed(2);
            }
          }
        });

        // Remove items that are no longer in cart
        const panelItems = panel.querySelectorAll('.fyncall-cart-panel-item');
        panelItems.forEach(itemEl => {
          const variantId = itemEl.getAttribute('data-variant-id');
          const inCart = cart.items.some(i => String(i.variant_id) === variantId);
          if (!inCart) {
            console.log(`[FyncallWidget] Removing item ${variantId} - no longer in cart`);
            itemEl.remove();
          }
        });

        // Update header count
        const headerCount = panel.querySelector('.fyncall-cart-panel-count');
        if (headerCount) {
          headerCount.textContent = `${cart.item_count} item${cart.item_count !== 1 ? 's' : ''}`;
        }

        // Update totals
        _updateCartPanelTotals(panel, cart, 75);
      });
    },


    // =====================================================================
    // CART RENDERING - Delegates to cart-rendering.js module
    // =====================================================================
    renderCartItems: function() {
      const items = this.cartData.items;
      const hasItems = items && items.length > 0;

      if (!hasItems) {
        // Empty cart state - use module for HTML
        this.elements.cartItems.innerHTML = createEmptySideCartHTML();
        _updateSideCartDisplay(this.elements, {
          hasItems: false,
          cartCount: 0
        });
        return;
      }

      // Render cart items using module
      const totalSavings = _renderSideCartItems(items, this.elements.cartItems, {
        onQuantityChange: (action, key, qty) => this.handleCartItemAction(action, key, qty),
        onSaveForLater: (title) => this.saveItemForLater(title)
      });

      // Update free shipping progress
      this.updateFreeShippingProgress();

      // Update display
      _updateSideCartDisplay(this.elements, {
        hasItems: true,
        cartCount: this.cartCount,
        totalSavings: totalSavings,
        promoDiscount: this.appliedPromoDiscount || 0,
        subtotal: this.cartData.total
      });

      // Update delivery estimate based on cart
      this.updateDeliveryEstimate();
    },

    // Free shipping progress bar
    // Free shipping progress - delegates to cart-operations module
    updateFreeShippingProgress: function() {
      const result = _calculateFreeShippingProgress(this.cartData.total);

      this.elements.cartShippingProgress.style.display = 'block';
      this.elements.cartShippingFill.style.width = result.progress + '%';
      this.elements.cartShippingText.textContent = result.message;
      this.elements.cartShipping.textContent = result.shippingText;

      if (result.qualifies) {
        this.elements.cartShippingText.classList.add('achieved');
      } else {
        this.elements.cartShippingText.classList.remove('achieved');
      }
    },

    // Update delivery estimate - delegates to cart-operations module
    updateDeliveryEstimate: function() {
      const estimate = _calculateDeliveryEstimate();
      this.elements.cartDeliveryDate.textContent = estimate.rangeText;
    },

    // Toggle promo code form
    togglePromoForm: function() {
      this.elements.cartPromoForm.classList.toggle('show');
      if (this.elements.cartPromoForm.classList.contains('show')) {
        this.elements.cartPromoInput.focus();
      }
    },

    // Apply promo code - uses cart-operations module
    applyPromoCode: async function() {
      const validation = _validatePromoCode(this.elements.cartPromoInput.value);
      if (!validation.valid) {
        this.elements.cartPromoError.textContent = validation.error;
        this.elements.cartPromoError.style.display = 'block';
        return;
      }

      const code = validation.code;
      this.elements.cartPromoBtn.disabled = true;
      this.elements.cartPromoBtn.textContent = 'Applying...';
      this.elements.cartPromoError.style.display = 'none';

      const result = await applyPromoCodeToShopify(code);

      if (result.success) {
        this.appliedPromoCode = code;
        this.appliedPromoDiscount = _calculatePromoDiscount(this.cartData.total);
        this.elements.cartPromoForm.innerHTML = generateAppliedPromoHTML(code);

        // Bind remove button
        const removeBtn = this.elements.cartPromoForm.querySelector('[data-action="remove-promo"]');
        if (removeBtn) removeBtn.onclick = () => this.removePromoCode();

        this.renderCartItems();
        this.showStatus('Promo code applied!', 'success');
      } else {
        this.elements.cartPromoError.textContent = 'Invalid code. Ask our assistant for help!';
        this.elements.cartPromoError.style.display = 'block';
      }

      this.elements.cartPromoBtn.disabled = false;
      this.elements.cartPromoBtn.textContent = 'Apply';
    },

    // Remove promo code
    removePromoCode: function() {
      this.appliedPromoCode = null;
      this.appliedPromoDiscount = 0;

      // Reset promo form using module HTML
      this.elements.cartPromoForm.innerHTML = createPromoFormHTML();
      this.elements.cartPromoForm.classList.remove('show');

      // Re-bind events
      const newInput = this.elements.cartPromoForm.querySelector('.fyncall-cart-promo-input');
      const newBtn = this.elements.cartPromoForm.querySelector('.fyncall-cart-promo-btn');
      this.elements.cartPromoInput = newInput;
      this.elements.cartPromoBtn = newBtn;

      newBtn.onclick = () => this.applyPromoCode();
      newInput.onkeypress = (e) => {
        if (e.key === 'Enter') this.applyPromoCode();
      };

      this.renderCartItems();
      this.showStatus('Promo code removed', 'info');
    },

    // Save item for later (sends message to AI)
    saveItemForLater: function(itemTitle) {
      const message = `Save "${itemTitle}" to my wishlist for later`;
      this.sendMessage(message);
      this.showStatus('Asking assistant to save item...', 'info');
    },

    // Cart item creation - delegates to cart-rendering.js module
    createCartItem: function(item) {
      return _createSideCartItem(item, {
        onQuantityChange: (action, key, qty) => this.handleCartItemAction(action, key, qty),
        onSaveForLater: (title) => this.saveItemForLater(title)
      });
    },

    handleCartItemAction: async function(action, key, currentQty) {
      let newQty = currentQty;
      if (action === 'increase') newQty = currentQty + 1;
      else if (action === 'decrease') newQty = Math.max(0, currentQty - 1);
      else if (action === 'remove') newQty = 0;

      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: newQty })
        });
        if (response.ok) {
          await this.loadCart();
          if (newQty === 0) {
            this.showStatus('Item removed from cart', 'success');
          }
        }
      } catch (error) {
        console.error('[FyncallWidget] Cart update failed:', error);
        this.showStatus('Failed to update cart', 'error');
      }
    },

    handleCheckout: function() {
      // Use imported Shopify module for checkout
      _goToCheckout();
    },

    // =====================================================
    // SHOPIFY CART MODIFICATION METHODS (delegated to module)
    // =====================================================

    updateCartItemQuantity: async function(variantId, quantity) {
      try {
        const cart = await _shopifyUpdateCartItem(variantId, quantity);
        if (cart) {
          this.cartData = { items: cart.items || [], total: cart.total_price / 100 || 0 };
          this.cartCount = cart.item_count || 0;
          this.updateCartBadge();
          return { success: true, cart: this.cartData };
        }
        return { success: false, error: 'Failed to update cart' };
      } catch (error) {
        console.error('[FyncallWidget] Cart update error:', error);
        return { success: false, error: error.message };
      }
    },

    removeCartItem: async function(variantId) {
      return this.updateCartItemQuantity(variantId, 0);
    },

    clearCart: async function() {
      try {
        const cart = await _clearShopifyCart();
        this.cartData = { items: [], total: 0 };
        this.cartCount = 0;
        this.updateCartBadge();
        return { success: true, cart: this.cartData };
      } catch (error) {
        console.error('[FyncallWidget] Cart clear error:', error);
        return { success: false, error: error.message };
      }
    },

    handleCartAction: async function(cartAction) {
      const result = await _handleShopifyCartAction(cartAction, {
        onShowCheckout: () => this.showCheckoutButton(),
        onShowStatus: (msg, type) => this.showStatus(msg, type)
      });
      if (result?.cart) {
        this.cartData = { items: result.cart.items || [], total: result.cart.total_price / 100 || 0 };
        this.cartCount = result.cart.item_count || 0;
        this.updateCartBadge();
      }
      return result;
    },

    /**
     * Show a checkout button in the chat
     * Called when AI returns cart_action: checkout
     * Delegates to cart-rendering.js module
     */
    showCheckoutButton: function() {
      console.log('[FyncallWidget] Showing checkout button');

      // Create checkout button using module (injects styles automatically)
      const buttonContainer = _createCheckoutButton(() => this.handleCheckout());

      // Add to messages container
      this.elements.messages.appendChild(buttonContainer);
      this.scrollToBottom();
    },

    // =====================================================
    // VARIANT MODAL METHODS
    // =====================================================


    // =====================================================================
    // VARIANT MODAL - Delegates to variant-modal.js module
    // =====================================================================
    openVariantModal: function(product) {
      const state = openVariantModalState(product);
      this.currentVariantProduct = state.product;
      this.selectedVariant = state.variant;
      this.selectedQuantity = state.quantity;
      this.renderVariantModal();
      this.elements.variantModal.classList.add('show');
    },

    closeVariantModal: function() {
      this.elements.variantModal.classList.remove('show');
      closeVariantModalState();
      this.currentVariantProduct = null;
      this.selectedVariant = null;
      this.selectedQuantity = 1;
    },

    renderVariantModal: function() {
      const product = this.currentVariantProduct;
      if (!product) return;

      const html = generateVariantModalHTML(product, this.selectedVariant, this.selectedQuantity);
      this.elements.variantModalBody.innerHTML = html;

      // Bind option selection events
      this.elements.variantModalBody.querySelectorAll('.fyncall-variant-option:not(.disabled)').forEach(btn => {
        btn.onclick = (e) => {
          const optionName = e.target.dataset.option;
          const value = e.target.dataset.value;
          this.selectVariantOption(optionName, value);
        };
      });

      // Bind quantity events
      const inventoryQty = this.selectedVariant?.inventory_quantity ?? product.inventory_quantity;
      this.elements.variantModalBody.querySelectorAll('.fyncall-quantity-btn').forEach(btn => {
        btn.onclick = (e) => {
          const action = e.target.dataset.action;
          const maxQty = Math.min(inventoryQty || 99, 10);
          this.selectedQuantity = _updateQuantity(action, maxQty);
          this.renderVariantModal();
        };
      });

      // Bind size guide
      const sizeGuideBtn = this.elements.variantModalBody.querySelector('[data-action="size-guide"]');
      if (sizeGuideBtn) {
        sizeGuideBtn.onclick = () => this.showSizeGuide();
      }

      // Bind notify me
      const notifyBtn = this.elements.variantModalBody.querySelector('[data-action="notify-me"]');
      if (notifyBtn) {
        notifyBtn.onclick = () => this.handleNotifyMe();
      }
    },

    // Handle notify me form submission - delegates to variant-modal module
    handleNotifyMe: function() {
      const emailInput = this.elements.variantModalBody.querySelector('#fyncall-notify-email-input');
      const result = handleVariantNotifyMe(emailInput, (msg) => this.sendMessage(msg));

      if (!result.success) {
        if (emailInput) emailInput.style.borderColor = '#dc2626';
        return;
      }

      // Show success state
      const form = this.elements.variantModalBody.querySelector('.fyncall-notify-available');
      if (form) {
        form.innerHTML = `<div class="fyncall-notify-success">✓ We'll notify you at ${result.email} when this item is available!</div>`;
      }
    },

    // Show size guide - delegates to variant-modal module
    showSizeGuide: function() {
      requestSizeGuide((msg) => this.sendMessage(msg));
    },

    // Delegates to variant-modal module
    groupVariantOptions: function(variants) {
      return _groupVariantOptions(variants);
    },

    findVariantByOption: function(variants, optionName, value) {
      return _findVariantByOption(variants, optionName, value);
    },

    isOptionSelected: function(optionName, value) {
      return _isOptionSelected(optionName, value);
    },

    selectVariantOption: function(optionName, value) {
      this.selectedVariant = _selectVariantOption(optionName, value);
      if (this.selectedVariant) {
        this.renderVariantModal();
      }
    },

    getColorCode: function(colorName) {
      return _getColorCode(colorName);
    },

    handleVariantAddToCart: async function() {
      const details = getAddToCartDetails();
      if (!details) return;

      const { product, variantId, quantity, publicUrl } = details;
      if (!variantId) {
        if (publicUrl) window.open(publicUrl, '_blank');
        this.closeVariantModal();
        return;
      }

      const addBtn = this.elements.variantAdd;
      addBtn.disabled = true;
      addBtn.innerHTML = '<svg class="spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/></svg> Adding...';

      try {
        await _addToCart(variantId, quantity);
        this.cartCount += quantity;
        this.updateCartBadge();
        this.showStatus(`Added ${quantity}x "${product.name}" to cart!`, 'success');
        this.closeVariantModal();
      } catch (error) {
        console.error('[FyncallWidget] Add to cart error:', error);
        this.showStatus('Failed to add item. Please try again.', 'error');
        if (publicUrl) window.open(publicUrl, '_blank');
      } finally {
        addBtn.disabled = false;
        addBtn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 20c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zM20 20c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zM1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg> Add to Cart';
      }
    },

    // =====================================================================
    // UTILITIES
    // =====================================================================

    scrollToBottom: function() {
      this.elements.messages.scrollTop = this.elements.messages.scrollHeight;
    },

    getSessionId: function() { return this.session; }
  };

  window.FyncallWidgetSDK = FyncallWidgetSDK;

  // Auto-initialize based on mode
  if (window.FyncallApp && window.FyncallApp.token) {
    // SDK mode (running in iframe or direct loading) - get config from FyncallApp
    console.log('[FyncallWidget] SDK mode detected, initializing with FyncallApp config');
    FyncallWidgetSDK.init({
      token: window.FyncallApp.token,
      widgetId: window.FyncallApp.widgetId,
      baseUrl: window.FyncallApp.apiBaseUrl,
      ...window.FyncallApp.config
    });
  } else if (window.FyncallWidget) {
    // Standalone mode - get config from FyncallWidget global
    console.log('[FyncallWidget] Standalone mode detected, initializing with FyncallWidget config');
    FyncallWidgetSDK.init(window.FyncallWidget);
  }
})();
